import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { format } from "date-fns";
import {
  Tabs,
  Tab,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  DialogActions,
  DialogTitle,
  DialogContent,
  Dialog,
  InputAdornment,
  Switch,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import BuildIcon from "@mui/icons-material/Build";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Search as SearchIcon,
  GridView as GridIcon,
  ViewList as ListIcon,
  Add as AddIcon,
  KeyboardArrowDown as ChevronDownIcon,
  Sort as SortIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
  Description as DescriptionIcon,
  WhatsApp as WhatsAppIcon,
  RestorePageRounded,
  Close,
  InfoOutlined,
  ArrowBack,
} from "@mui/icons-material";
import {
  Grid,
  Typography,
  Container,
  TextField,
  Button,
  Alert,
  IconButton,
  Autocomplete,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  FormControl,
  CircularProgress,
  Checkbox,
  Box,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  useMediaQuery,
  Paper,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import OneTimeBroadcast from "./BroadcastTypes/OneTimeBroadcast";
import RecurringBroadcast from "./BroadcastTypes/RecurringBroadcast";
import RetryFailedBroadcast from "./BroadcastTypes/RetryFailedBroadcast";
import DeleteIcon from "@mui/icons-material/Delete";
import MessageNode from "../Whatsapp/MessageNode";
import { getMpmProductCount, getMpmThumbnailImage, getSpmProductName } from "../Whatsapp/mpmPreviewUtils";
import { authPreviewProps } from "../Whatsapp/authTemplateUtils";
import {
  TEMPLATE_ENDPOINT,
  TEMPLATE_BROADCAST_ENDPOINT,
  CAMPAIGN_LIST_ENDPOINT,
  VALIDATE_WALLET_ENDPOINT,
  EDIT_SCHEDULED_BROADCAST,
} from "../../config/urls";
import UploadCSV from "./CampaignOptions/UploadCSV";
import SingleContact from "./CampaignOptions/SingleContact";
import SelectSegment from "./CampaignOptions/SelectSegment";
import axios from "axios";
import { useAuth } from "../../hooks/AuthProvider";
import TemplateGridCard from "./TemplateGridCard";
import TemplateListCard from "./TemplateListCard";
import { ColorsConstants } from "../../cssUtils/constants";
import { useLocation } from "react-router-dom";
import ButtonOne from "../../common/normal-button";
import WalletErrorDialog from "../../common/wallet-balance-modal";
import TimeSlotDropdown from "./CampaignOptions/SelectTimeSlot";
import ButtonTwo from "../../common/dashboard-button";
import CustomTabs from "../../common/custom-tabs";
import { ReactComponent as Upload } from "../../assets/images/icons/campaign/broadcast/upload.svg";
import { ReactComponent as Segment } from "../../assets/images/icons/campaign/broadcast/segment.svg";
import { ReactComponent as Contact } from "../../assets/images/icons/campaign/broadcast/single-contact.svg";
import { ReactComponent as Now } from "../../assets/images/icons/campaign/broadcast/now.svg";
import { ReactComponent as Schedule } from "../../assets/images/icons/campaign/broadcast/schedule.svg";
import StepBroadcastSelection from "./BroadcastTypes/OneTimeBroadcast";
import AppStepper from "../../common/app-stepper";
import TemplateMessagePreview from "../CampaignsV2/TemplateMessagePreview";
import BroadcastSections, { AudienceTypePicker, EMPTY_FALLBACK, VariablesPanel, fallbackError, fallbackFields } from "../CampaignsV2/BroadcastSections";
import typography from "../../cssUtils/typography";
import TableSearchInput from "../TableSearchInput";
import AppDropdown from "../../common/app-dropdown";
import BackButton from "../../common/back-button";
import CampaignTypeModal from "../CampaignManagement/CampaignTypeModal";
import CreateCampaignModal from "../CampaignManagement/CreateCampaignModal";

const radioSx = {
  color: ColorsConstants.primaryBlue100,
  "&.Mui-checked": {
    color: ColorsConstants.primaryBlue100,
  },
};

const LARGE_BROADCAST_TIMEOUT_IN_SECONDS = 45;
const AUTO_NAVIGATION_DELAY_MS = 3000;

// Step 1 Component: Template Selection (only for non-retry)
const StepTemplateSelection = ({
  categoryFilter,
  fromTables,
  props,
  filteredAndSortedTemplates,
  templatesLoading,
  viewMode,
  sortAnchorEl,
  setSortAnchorEl,
  sortBy,
  setSortBy,
  currentSortLabel,
  sortOptions,
  searchQuery,
  setSearchQuery,
  selectedTemplate,
  templateCardRefs,
  handleTemplateChange,
  formatReadableDate,
  handleTemplateSelect,
  isEditMode,
  scheduleDate,
  setScheduleDate,
  broadcastTime,
  setBroadcastTime,
  handleCampaignClick,
}) => (
  <>
    <Container maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          py: 1.5,
        }}
      >
        {/* Search - Using TableSearchInput */}
        <Box sx={{ width: "75%" }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
            charCount={0} // If you don't need character count, you can pass 0 or manage it separately
            tooltip="Search templates..."
            placeholder="Search templates…"
          />
        </Box>

        <AppDropdown
          startIcon={<SortIcon />}
          endIcon={<ChevronDownIcon />}
          selectedValue={sortBy}
          onMenuSelect={(value) => setSortBy(value)}
          menuItems={sortOptions}
          paperProps={{
            elevation: 0,
            style: {
              minWidth: "160px",
              border: "1px solid #DCDFE5",
              borderRadius: "8px",
            },
          }}
          sx={{
            borderRadius: "8px",
            border: "1px solid #D1D5DC",
            height: 40,
            minHeight: 40,
            width: { xs: "25%", sm: "auto" },
            flexShrink: 0,
            ...typography.bodyLarge,
          }}
        >
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            {currentSortLabel ? `Sort by: ${currentSortLabel}` : "Sort by"}
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>Sort</Box>
        </AppDropdown>
      </Box>
    </Container>

    <Grid item xs={12}>
      {templatesLoading ? (
        <CircularProgress color="primary" />
      ) : (
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box
            sx={{
              width: "100%",
              height: 670,
              overflowY: "auto",
              overflowX: "hidden",
              border: "1px solid #eee",
              borderRadius: 1,
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignContent: "flex-start",
                margin: 0,
                width: "100%",
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                },
                "& > .MuiGrid-item": {
                  paddingLeft: 2,
                  paddingTop: 2,
                },
              }}
            >
              {filteredAndSortedTemplates.map((template) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={3}
                  key={template.template_id}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <TemplateGridCard
                    onClick={() => handleTemplateSelect(template)}
                    ref={(el) => {
                      templateCardRefs.current[template.template_name] = el;
                    }}
                    name={template.template_name}
                    id={template.template_id}
                    status={template.status}
                    category={template.category}
                    language={template?.template_json?.languageCode}
                    date={formatReadableDate(template.updated_at)}
                    hasMedia={!!template?.template_preview_json?.headerFile}
                    mediaFile={template?.template_preview_json?.headerFile}
                    message={
                      authPreviewProps(template.template_preview_json).body ??
                      template?.template_preview_json?.body
                    }
                    footer={
                      authPreviewProps(template.template_preview_json).footer ??
                      template?.template_preview_json?.footer
                    }
                    selected={selectedTemplate === template.template_name}
                    template_json={template.template_json}
                    template_preview_json={{
                      ...template.template_preview_json,
                      ...authPreviewProps(template.template_preview_json),
                    }}
                    showSampleValues={false}
                    sampleBodyFields={
                      authPreviewProps(template.template_preview_json).sampleBodyFields ??
                      (template?.template_preview_json?.sampleBodyFields || [])
                    }
                    sampleHeaderFields={
                      template?.template_preview_json?.sampleHeaderFields || []
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      )}
    </Grid>
  </>
);

// Step 2 Component: Campaign Type Selection
const StepCampaignSelection = ({
  selectedCampaign,
  setSelectedCampaign,
  selectedTemplate,
  csvFile,
  setCsvFile,
  campaignsData,
  selectedSegment,
  setSelectedSegment,
  phoneNumberCount,
  setPhoneNumberCount,
  setCountryCode,
  setDialCode,
  setName,
  name,
  countryCode,
  dialCode,
  csvInputRef,
  isEditMode,
  props,
  campaignType,
  audienceError,
  setAudienceError,
}) => {
  const cardStyle = {
    p: 2,
    borderRadius: 2,
    mb: 2,
    cursor: isEditMode ? "not-allowed" : "pointer",
    opacity: isEditMode ? 0.6 : 1,
    backgroundColor: "#F6F6F6",
    transition: "all 0.2s ease",
    boxShadow: "none",
    border: "1px solid #E5E7EB",
    "&:hover": {
      borderColor: isEditMode
        ? "#E5E7EB"
        : ColorsConstants.primaryBackgroundColor,
    },
  };

  const selectedCardStyle = {
    ...cardStyle,
    borderColor: ColorsConstants.primaryBackgroundColor,
    backgroundColor: "#F0F7FF",
    borderWidth: "2px",
  };

  console.log(campaignType, "campain type");
  return (
    <Box>
      <Typography sx={{ ...typography.h2, mt: 2, mb: 1 }}>
        Select Campaign Type:
      </Typography>

      <RadioGroup
        value={selectedCampaign}
        onChange={(e) => setSelectedCampaign(e.target.value)}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Option 1 - CSV Upload */}
        <Paper
          sx={{
            ...(selectedCampaign === "option1" ? selectedCardStyle : cardStyle),
            flex: 1,
            minWidth: "30%",
            maxWidth: "calc(33.33% - 8px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
          }}
          onClick={() =>
            !(isEditMode || campaignType === "quick") &&
            setSelectedCampaign("option1")
          }
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Upload style={{ width: 24, height: 24, color: "#4B5563" }} />

            <Radio
              value="option1"
              checked={selectedCampaign === "option1"}
              disabled={isEditMode || campaignType === "quick"}
              sx={{
                color: "#D1D5DB",
                "&.Mui-checked": {
                  color: ColorsConstants.primaryBackgroundColor,
                },
                p: 0.5,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                ...typography.bodyLarge,
                fontWeight: 600,
                color: "#505050",
                mb: "6px",
              }}
            >
              Upload Audience using CSV
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: "#505050" }}>
              Upload Audience using CSV
            </Typography>
          </Box>
        </Paper>

        {/* Option 2 - Segments */}
        <Paper
          sx={{
            ...(selectedCampaign === "option2" ? selectedCardStyle : cardStyle),
            flex: 1,
            minWidth: "30%",
            maxWidth: "calc(33.33% - 8px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
          }}
          onClick={() =>
            !(isEditMode || campaignType === "quick") &&
            setSelectedCampaign("option2")
          }
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Segment style={{ width: 24, height: 24, color: "#4B5563" }} />

            <Radio
              value="option2"
              checked={selectedCampaign === "option2"}
              disabled={isEditMode || campaignType === "quick"}
              sx={{
                color: "#D1D5DB",
                "&.Mui-checked": {
                  color: ColorsConstants.primaryBackgroundColor,
                },
                p: 0.5,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                ...typography.bodyLarge,
                fontWeight: 600,
                color: "#505050",
                mb: "6px",
              }}
            >
              Send using Segments
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
              Choose from predefined audience segments
            </Typography>
          </Box>
        </Paper>

        {/* Option 3 - Single Contact */}
        <Paper
          sx={{
            ...(selectedCampaign === "option3" ? selectedCardStyle : cardStyle),
            flex: 1,
            minWidth: "30%",
            maxWidth: "calc(33.33% - 8px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
          }}
          onClick={() =>
            !(isEditMode || campaignType === "standard") &&
            setSelectedCampaign("option3")
          }
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Contact style={{ width: 24, height: 24, color: "#4B5563" }} />

            <Radio
              value="option3"
              checked={selectedCampaign === "option3"}
              disabled={isEditMode || campaignType !== "quick"}
              sx={{
                color: "#D1D5DB",
                "&.Mui-checked": {
                  color: ColorsConstants.primaryBackgroundColor,
                },
                p: 0.5,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                ...typography.bodyLarge,
                fontWeight: 600,
                color: "#505050",
                mb: "6px",
              }}
            >
              Send to single Contact
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
              Send to one specific Phone number
            </Typography>
          </Box>
        </Paper>
      </RadioGroup>

      <Divider sx={{ width: "100%", mt: 2 }} />

      {selectedCampaign === "option1" && (
        <UploadCSV
          ref={csvInputRef}
          csvFile={csvFile}
          setCsvFile={setCsvFile}
          campaignsData={campaignsData}
          isEditMode={isEditMode}
        />
      )}

      {selectedCampaign === "option2" && (
        <SelectSegment
          audienceError={audienceError}
          setAudienceError={setAudienceError}
          prefetchedSegmentName={props?.pageParams?.segmentName}
          isEditMode={isEditMode}
          selectedSegment={selectedSegment}
          setSelectedSegment={setSelectedSegment}
          phoneNumberCount={phoneNumberCount}
          setPhoneNumberCount={setPhoneNumberCount}
          campaignsData={campaignsData}
        />
      )}

      {selectedCampaign === "option3" && (
        <SingleContact
          setCountryCode={setCountryCode}
          setDialCode={setDialCode}
          setName={setName}
          nameValue={name}
          countryCodeValue={countryCode}
          phoneNumberValue={dialCode}
          isEditMode={isEditMode}
        />
      )}
    </Box>
  );
};

// Template Preview Modal Component

const TemplatePreviewModal = ({
  open,
  onClose,
  templatePreviewData,
  carouselMediaFiles,
  renderCustomFields,
  FileUploadDetails,
  selectedTemplate,
  handleCampaignClick,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxHeight: "90vh",
          width: 483,
        },
      }}
    >
      <DialogContent
        dividers
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #E5E7EB",
          bgcolor: "#F9FAFB",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <TemplateMessagePreview
            templatePreviewData={templatePreviewData}
            carouselMediaFiles={carouselMediaFiles}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
          }}
        >
          <FileUploadDetails />
          {renderCustomFields()}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "#F9FAFB",
        }}
      >
        <ButtonTwo
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: "#D1D5DB",
            minWidth: 85,
            color: "#6B7280",
            "&:hover": {
              borderColor: "#9CA3AF",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </ButtonTwo>
        <ButtonOne
          variant="contained"
          onClick={(e) => {
            handleCampaignClick(e);
            onClose();
          }}
          sx={{
            minWidth: 85,
          }}
        >
          Next
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

const BroadcastTemplates = (props) => {
  // Get campaign name and description from props (passed from modal)
  const {
    campaignName: initialCampaignName = "",
    campaignDescription: initialCampaignDescription = "",
    campaignType: initalCampaignType = null,
  } = props?.pageParams || {};

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplateForModal, setSelectedTemplateForModal] =
    useState(null);
  // Modal state for WhatsApp campaign creation
  const [campaignTypeModalOpen, setCampaignTypeModalOpen] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const handleTemplateSelect = (template) => {
    setSelectedTemplateForModal(template);
    handleTemplateChange({ target: { value: template.template_name } });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTemplateForModal(null);
  };

  const parseScheduledDateTime = (scheduled) => {
    if (!scheduled) return { date: null, time: null };
    const parsed = new Date(scheduled);
    if (isNaN(parsed.getTime())) {
      return { date: null, time: null };
    }
    const date = new Date(parsed);
    date.setHours(0, 0, 0, 0);
    const time = new Date();
    time.setHours(parsed.getHours(), parsed.getMinutes(), 0, 0);
    return { date, time };
  };

  const getInitialDateTime = () => {
    if (props?.pageParams?.isEdit && props?.pageParams?.broadcast?.scheduled) {
      return parseScheduledDateTime(props.pageParams.broadcast.scheduled);
    }
    return { date: "", time: "" };
  };

  const { getValidToken } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templateData, setTemplateData] = useState(null);
  const [templatePreviewData, setTemplatePreviewData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [requiredFields, setRequiredFields] = useState({});
  const fileInputRef = useRef(null);
  const [mediaType, setMediaType] = useState("none");
  const [mediaFile, setMediaFile] = useState(null);
  const [requiredFieldsToRender, setRequiredFieldsToRender] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [showCampaignOptions, setShowCampaignOptions] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [campaignsData, setCampaignsData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  // Campaign name and description are now from props (from modal)
  const [campaignName, setCampaignName] = useState(initialCampaignName);
  const [campaignDescription, setCampaignDescription] = useState(
    initialCampaignDescription,
  );
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedSegmentList, setSelectedSegmentList] = useState([]);
  const [phoneNumberCount, setPhoneNumberCount] = useState(0);
  const [countryCode, setCountryCode] = useState("");
  const [dialCode, setDialCode] = useState("");
  const [walletErrorAcknowledged, setWalletErrorAcknowledged] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
  });
  const [walletErrorDialog, setWalletErrorDialog] = useState({
    open: false,
    data: null,
  });
  const [csvError, setCSVError] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [csvErrorAcknowledged, setCsvErrorAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSlowProcessing, setIsSlowProcessing] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successResponse, setSuccessResponse] = useState(false);
  const location = useLocation();
  const [stepOneError, setStepOneError] = useState(false);
  const [isEditMode, setEditMode] = useState(
    props?.pageParams?.isEdit || false,
  );
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [broadcastType, setBroadcastType] = useState(
    isEditMode ? "one-time" : "now",
  );
  const [scheduleDate, setScheduleDate] = useState(
    getInitialDateTime().date || "",
  );
  const [broadcastTime, setBroadcastTime] = useState(
    getInitialDateTime().time || "",
  );
  const [notifyBefore, setNotifyBefore] = useState(true);
  const [notifyStatus, setNotifyStatus] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("marketing");
  const [name, setName] = useState("");
  const [walletValidation, setWalletValidation] = useState(null);
  const [recurringData, setRecurringData] = useState({});
  const [isRetryEdit, setIsRetryEdit] = useState(
    props?.pageParams?.option === "Retry",
  );
  const [fromTables, setFromTables] = useState(
    props?.pageParams?.fromTemplatesTable || false,
  );
  const [fromBroadcastCampaign, setFromBroadcastCampaign] = useState(
    props?.pageParams?.lastPage || false,
  );
  // Campaigns (Beta): `singlePage` renders this form as one page inside the Campaigns (Beta) setup page;
  // otherwise campaign/broadcast values arrive through pageParams when opened from there
  const singlePage = props?.singlePage || null;
  const campaignsV2Params = singlePage || props?.pageParams || {};
  const templateFormRef = useRef(null);

  // Opened from Campaigns (Beta): return there instead of the Broadcast page
  const goToBroadcastList = (params) => {
    if (singlePage || props?.pageParams?.lastPage === "campaigns_v2") {
      props.onNavigate("campaigns_v2", { tab: campaignsV2Params.returnTab });
    } else {
      props.onNavigate("marketing_broadcast", params);
    }
  };

  console.log(props?.pageParams, "Props pageParams");
  const [enableTimeSlot, setEnableTimeSlot] = useState(false);
  const [timeSlot, setTimeSlot] = useState("24");
  // Campaigns (Beta): fallback to RCS for failed recipients (instead of smart retry)
  const [fallback, setFallback] = useState(EMPTY_FALLBACK);

  useEffect(() => {
    if (!singlePage) return;
    setCampaignName(singlePage.campaignName || "");
    setCampaignDescription(singlePage.campaignDescription || "");
    setCampaignType(singlePage.quick ? "quick" : "standard");
    if (singlePage.quick) setSelectedCampaign("option3");
    setIsCampaignTypeReady(true);
    setShowCampaignOptions(true);
  }, [singlePage?.campaignName, singlePage?.campaignDescription, singlePage?.quick]);
  const csvInputRef = useRef(null);
  const templateCardRefs = useRef({});
  const [campaignType, setCampaignType] = useState(initalCampaignType || "");
  const [campaignNameError, setCampaignNameError] = useState("");
  const [campaignNameCharCount, setCampaignNameCharCount] = useState(0);
  const [campaignDescCharCount, setCampaignDescCharCount] = useState(0);
  const [isCampaignTypeReady, setIsCampaignTypeReady] = useState(false);
  const [audienceError, setAudienceError] = useState(false);
  // Step management - only used for non-retry flows
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Select Template", "Select Campaign", "Select Broadcast Type"];

  // Default campaign type to single contact for quick campaign
  useEffect(() => {
    if (initalCampaignType === "quick") {
      setSelectedCampaign("option3");
    }
  }, [initalCampaignType]);

  useEffect(() => {
    if (props?.pageParams?.fromTemplatesTable && apiData?.results?.length > 0) {
      setCategoryFilter(props?.pageParams?.templateCategory?.toLowerCase());
      setSelectedTemplate(props.pageParams.templateName);

      const selectedTemplate = apiData?.results?.find(
        (item) => item.template_name === props.pageParams.templateName,
      );

      if (selectedTemplate) {
        setTemplatePreviewData(selectedTemplate.template_preview_json);
        setTemplateData(selectedTemplate.template_json);
        setMediaType("none");
        updateRequiredFields(
          selectedTemplate.template_name,
          selectedTemplate.template_preview_json,
        );
      }
    }
  }, [props?.pageParams?.fromTemplatesTable, apiData]);

  useEffect(() => {
    if (selectedTemplate && templateCardRefs.current[selectedTemplate]) {
      setTimeout(() => {
        templateCardRefs.current[selectedTemplate]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }, 100);
    }
  }, [selectedTemplate, templatesLoading]);

  useEffect(() => {
    if (isEditMode) {
      if (props.pageParams?.option === "Single") {
        setSelectedCampaign("option3");
        const fullPhone =
          props.pageParams?.audience?.audience_phone_numbers?.[0];
        const { countryCode, phone } = splitPhoneNumber(fullPhone);
        setCountryCode(countryCode);
        setDialCode(phone);
      } else if (props.pageParams?.option === "Segment") {
        setSelectedCampaign("option2");
      } else if (props.pageParams?.option === "CSV") {
        setSelectedCampaign("option1");
      }
      setShowCampaignOptions(true);
      setBroadcastType("one-time");
      setCampaignName(props?.pageParams?.campaignName || initialCampaignName);
      setCampaignDescription(
        props?.pageParams?.campaignDescription || initialCampaignDescription,
      );
    }
  }, [
    isEditMode,
    props?.pageParams,
    initialCampaignName,
    initialCampaignDescription,
  ]);

  useEffect(() => {
    if (isEditMode && props?.pageParams?.templateName) {
      setSelectedTemplate(props.pageParams.templateName);

      const selectedTemplate = apiData?.results?.find(
        (item) => item.template_name === props.pageParams.templateName,
      );

      if (selectedTemplate) {
        setTemplatePreviewData(selectedTemplate.template_preview_json);
        setTemplateData(selectedTemplate.template_json);
        setMediaType("none");
        updateRequiredFields(
          selectedTemplate.template_name,
          selectedTemplate.template_preview_json,
        );
      }
    }
  }, [isEditMode, props?.pageParams, apiData]);

  useEffect(() => {
    if (isEditMode && props?.pageParams?.templateType) {
      const mappedTab = TEMPLATE_TYPE_TO_TAB[props.pageParams.templateType];
      if (mappedTab) {
        setCategoryFilter(mappedTab);
      }
    }
  }, [isEditMode, props?.pageParams]);

  const [formErrors, setFormErrors] = useState({
    latitude: false,
    longitude: false,
  });

  const TEMPLATE_TYPE_TO_TAB = {
    MARKETING: "marketing",
    UTILITY: "utility",
    RETRY: "retry",
  };

  useEffect(() => {
    if (isEditMode) {
      // Editing an existing campaign - audience type can't be changed, so skip
      // the campaign type picker and land straight on the template step.
      setCampaignType(props?.pageParams?.option === "Single" ? "quick" : "standard");
      setIsCampaignTypeReady(true);
    } else if (initalCampaignType) {
      // Coming from previous screen
      setCampaignType(initalCampaignType);
      setIsCampaignTypeReady(true);
    } else {
      // Direct entry → force modal
      setCampaignTypeModalOpen(true);
    }
  }, []);

  console.log(campaignType, "Campaign type")

  const handleAlertClose = () =>
    setAlertObj({ active: false, type: "", msg: "" });
  const handleCSVErrorClose = () => setCSVError(null);
  const selectOptions = ["name", "age", "gender", "city", "email", "custom"];

  const handleRetryTemplatePreview = (templatePreviewData) => {
    setTemplatePreviewData(templatePreviewData);
  };
  const [carouselMediaFiles, setCarouselMediaFiles] = useState({});
  const carouselFileInputRefs = useRef({});
  const [carouselFileDisplays, setCarouselFileDisplays] = useState({});
  const [cardType, setCardType] = useState("");
  const [mediaFileError, setMediaFileError] = useState(false);
  const [carouselFileErrors, setCarouselFileErrors] = useState({});
  const [dripTemplates, setDripTemplates] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (selectedCampaign === "option3") {
      setName("N/A");
    }
  }, [selectedCampaign]);

  const convertFiltersToQueryParams = (filters) => {
    if (!filters || filters.length === 0) return new URLSearchParams({});
    let queryParams = new URLSearchParams({ limit: 1, offset: 0 });
    filters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(field, `${operator}:${value ?? ""}`);
    });
    return queryParams.toString();
  };

  const CarouselFileDisplay = ({ cardIndex, file, onDelete, fileError }) => {
    const hasError = carouselFileErrors[cardIndex];
    if (!file && !fileError) return null;
    return (
      <Grid container direction="column" spacing={1} sx={{ mt: 1 }}>
        {fileError && (
          <Grid item>
            <Typography variant="body2" color="error" sx={{ display: "block" }}>
              {fileError}
            </Typography>
          </Grid>
        )}
        {file && (
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="body2">
                  File uploaded: {file.name}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  aria-label="delete"
                  onClick={(e) => {
                    e.preventDefault();
                    onDelete(cardIndex);
                  }}
                  size="small"
                  type="button"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const CarouselFileDisplayContainer = ({ carouselMediaFiles, onDelete }) => {
    const [, forceRender] = useState({});
    useEffect(() => {
      forceRender({});
    }, [carouselMediaFiles]);
    return (
      <>
        {Object.entries(carouselMediaFiles).map(([cardIndex, file]) => (
          <div
            key={`file-display-${cardIndex}`}
            id={`carousel-file-display-content-${cardIndex}`}
          >
            <CarouselFileDisplay
              cardIndex={parseInt(cardIndex)}
              file={file}
              onDelete={onDelete}
            />
          </div>
        ))}
      </>
    );
  };

  const getCarouselFileInput = (type, cardIndex) => {
    const fileTypes = {
      image: { accept: ".jpg, .png", label: "Choose .JPG or .PNG *" },
      video: { accept: ".mp4", label: "Choose .MP4 file *" },
      document: { accept: ".pdf", label: "Choose .PDF file *" },
    };
    if (fileTypes[type]) {
      const { accept, label } = fileTypes[type];
      const inputId = `carousel-file-upload-${cardIndex}`;
      return (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <input
              ref={(el) => (carouselFileInputRefs.current[cardIndex] = el)}
              id={inputId}
              name={`carousel-file-${cardIndex}`}
              type="file"
              accept={accept}
              onChange={(e) => handleCarouselMediaFileChange(e, cardIndex)}
              style={{ display: "none" }}
              required
            />
            <label htmlFor={inputId}>
              <Button
                variant="contained"
                component="span"
                sx={{ marginTop: 1, marginBottom: 1 }}
                type="button"
              >
                {label}
              </Button>
            </label>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  const handleCarouselMediaFileChange = (event, cardIndex) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) return;

    let allowedTypes = [];
    const inputAccept = event.target.accept;
    if (inputAccept.includes("mp4")) {
      allowedTypes = ["video/mp4"];
    } else if (inputAccept.includes("pdf")) {
      allowedTypes = ["application/pdf"];
    } else {
      allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    }

    if (allowedTypes.length && !allowedTypes.includes(file.type)) {
      let typeMsg = "";
      if (allowedTypes[0] === "video/mp4") typeMsg = "video (.mp4)";
      else if (allowedTypes[0] === "application/pdf") typeMsg = "PDF (.pdf)";
      else typeMsg = "image (.jpg, .jpeg, .png)";
      setCarouselFileErrors((prev) => ({
        ...prev,
        [cardIndex]: `Invalid file type. Please upload a ${typeMsg} file.`,
      }));
      event.target.value = "";
      return;
    }

    const maxSizes = {
      "video/mp4": 16 * 1024 * 1024,
      "image/jpeg": 5 * 1024 * 1024,
      "image/jpg": 5 * 1024 * 1024,
      "image/png": 5 * 1024 * 1024,
      "application/pdf": 5 * 1024 * 1024,
    };

    const maxSize = maxSizes[file.type];
    if (maxSize && file.size > maxSize) {
      const sizeLimit = file.type === "video/mp4" ? "16MB" : "5MB";
      const fileType =
        file.type === "video/mp4"
          ? "video"
          : file.type === "application/pdf"
            ? "PDF"
            : "image";
      setCarouselFileErrors((prev) => ({
        ...prev,
        [cardIndex]: `File size exceeds the limit of ${sizeLimit} for ${fileType} files.`,
      }));
      event.target.value = "";
      return;
    }

    setCarouselFileErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[cardIndex];
      return newErrors;
    });

    setCarouselMediaFiles((prev) => ({
      ...prev,
      [cardIndex]: file,
    }));
  };

  const handleDeleteCarouselMediaFile = (cardIndex) => {
    setCarouselFileErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[cardIndex];
      return newErrors;
    });

    setCarouselMediaFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[cardIndex];
      return newFiles;
    });

    if (carouselFileInputRefs.current[cardIndex]) {
      carouselFileInputRefs.current[cardIndex].value = "";
    }
  };

  const handleMediaFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    let allowedTypes = [];
    if (mediaType === "image") {
      allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    } else if (mediaType === "video") {
      allowedTypes = ["video/mp4"];
    } else if (mediaType === "document") {
      allowedTypes = ["application/pdf"];
    } else if (mediaType === "gif") {
      allowedTypes = ["image/gif"];
    }

    if (allowedTypes.length && !allowedTypes.includes(file.type)) {
      let typeMsg = "";
      if (mediaType === "image") typeMsg = "image (.jpg, .jpeg, .png)";
      else if (mediaType === "video") typeMsg = "video (.mp4)";
      else if (mediaType === "document") typeMsg = "PDF (.pdf)";
      else if (mediaType === "gif") typeMsg = "GIF (.gif)";
      setMediaFileError(`Invalid file type. Please upload a ${typeMsg} file.`);
      event.target.value = "";
      return;
    }

    const maxSizes = {
      "video/mp4": 16 * 1024 * 1024,
      "image/jpeg": 5 * 1024 * 1024,
      "image/jpg": 5 * 1024 * 1024,
      "image/png": 5 * 1024 * 1024,
      "application/pdf": 100 * 1024 * 1024,
      "image/gif": 16 * 1024 * 1024,
    };

    const maxSize = maxSizes[file.type];
    if (maxSize && file.size > maxSize) {
      const sizeLimit =
        file.type === "video/mp4" || file.type === "image/gif"
          ? "16MB"
          : file.type === "application/pdf"
            ? "100MB"
            : "5MB";
      const fileType =
        file.type === "video/mp4"
          ? "video"
          : file.type === "application/pdf"
            ? "document"
            : file.type === "image/gif"
              ? "gif"
              : "image";
      setMediaFileError(
        `File size exceeds the limit of ${sizeLimit} for ${fileType} files.`,
      );
      event.target.value = "";
      return;
    }

    setMediaFileError(false);
    setMediaFile(file);
  };

  const handleDeleteMediaFile = () => {
    setMediaFileError(false);
    setMediaFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    handleTemplateChange({ target: { value: selectedTemplate || "" } });
  };

  const handleCategoryFilterChange = (event, newValue) => {
    const selected = newValue;
    if (selected !== categoryFilter) {
      // Reset all form states
      resetForm();

      // Reset campaign selection states
      setSelectedCampaign("");
      setCsvFile(null);
      setSelectedSegment(null);
      setPhoneNumberCount(0);
      setCountryCode("");
      setDialCode("");
      setName("");

      // Reset broadcast type states
      setBroadcastType(isEditMode ? "one-time" : "now");
      setEnableTimeSlot(false);
      setTimeSlot("24");
      setScheduleDate("");
      setBroadcastTime("");

      // Reset step to 0 only for non-retry
      setActiveStep(0);

      // Reset selected template
      setSelectedTemplate("");
      setTemplateData(null);
      setTemplatePreviewData(null);
      setRequiredFieldsToRender([]);

      // Reset any errors and alerts
      setAlertObj({ active: false, msg: "", type: "" });
      setCSVError(null);
      setScheduleError(null);

      // Reset form values
      setFormValues({});
      setMediaType("none");
      setMediaFile(null);
      setMediaFileError(false);
      setCarouselFileErrors({});
      setCarouselMediaFiles({});

      // Clear file input if any
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Reset showTemplateSelection
      setShowTemplateSelection(false);

      // Finally set the new category
      setCategoryFilter(selected);
    }
  };

  useEffect(() => {
    let isCancelled = false;
    const loadTemplates = async () => {
      if (!categoryFilter || categoryFilter === "retry") return;
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      setTemplatesLoading(true);
      try {
        const response = await fetch(
          `${TEMPLATE_ENDPOINT}?category_filter=${categoryFilter}&limit=1000&ordering=-last_edited`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          },
        );
        if (!response.ok) throw new Error("Failed to fetch templates data");
        const jsonData = await response.json();
        if (!isCancelled) {
          const approvedTemplates = {
            ...jsonData,
            results: jsonData.results.filter(
              (template) => template.status === "APPROVED" && template.active,
            ),
          };
          setApiData(approvedTemplates);
          setTemplateOptions(approvedTemplates.results.map((item) => item));
          setDripTemplates(() => {
            const _dripTemplates = {};
            approvedTemplates.results.forEach((item) => {
              _dripTemplates[item.template_name] = item.is_drip_template;
            });
            return _dripTemplates;
          });
          setRequiredFields(approvedTemplates.required_fields);
          setShowTemplateSelection(true);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching templates data:", error.message);
        }
      } finally {
        if (!isCancelled) {
          setTemplatesLoading(false);
        }
      }
    };
    loadTemplates();
    return () => {
      isCancelled = true;
    };
  }, [categoryFilter, getValidToken]);

  useEffect(() => {
    if (walletValidation?.status === "error") {
      setWalletErrorAcknowledged(false);
    }
  }, [walletValidation]);

  const splitPhoneNumber = (fullNumber) => {
    if (!fullNumber) return { countryCode: "", phone: "" };
    const knownCountryCodes = ["971", "91", "1"];
    for (const code of knownCountryCodes) {
      if (fullNumber.startsWith(code)) {
        return { countryCode: code, phone: fullNumber.slice(code.length) };
      }
    }
    return { countryCode: "", phone: fullNumber };
  };

  // WhatsApp Modal handlers
  const handleOpenCampaignTypeModal = () => setCampaignTypeModalOpen(true);
  const handleCloseCampaignTypeModal = () => setCampaignTypeModalOpen(false);
  const handleCloseCampaignModal = () => setCampaignModalOpen(false);
  // Shared form state

  const handleOpenCampaignModal = () => {
    setCampaignName("");
    setCampaignDescription("");
    setCampaignNameError("");
    setCampaignNameCharCount(0);
    setCampaignDescCharCount(0);
    setCampaignModalOpen(true);
  };

  // Shared Handlers
  const handleCampaignNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) {
      setCampaignName(value);
      setCampaignNameCharCount(value.length);
      setCampaignNameError(
        value.trim() === "" ? "Campaign name is required" : "",
      );
    }
  };

  const handleCampaignDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 250) {
      setCampaignDescription(value);
      setCampaignDescCharCount(value.length);
    }
  };

  // Submit for WhatsApp
  const handleCreateCampaign = () => {
    if (!campaignName.trim() && campaignType === "standard") {
      setCampaignNameError("Campaign name is required");
      return;
    }
    setCampaignModalOpen(false);
    setIsCampaignTypeReady(true);
  };

  const fetchCampaignsData = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    try {
      const response = await fetch(`${CAMPAIGN_LIST_ENDPOINT}?limit=1000`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch campaigns data");
      const jsonData = await response.json();
      const campaignNames = jsonData.results.map((campaign) =>
        campaign.campaign_name.toLowerCase(),
      );
      setCampaignsData(campaignNames);
    } catch (error) {
      console.error("Error fetching campaigns data:", error.message);
    }
  }, [getValidToken]);

  const handleTemplateChange = (event) => {
    const selectedTemplateName = event.target.value;
    resetForm();
    setAlertObj({ active: false, msg: "", type: "" });
    setCSVError(null);
    setSelectedTemplate(selectedTemplateName);

    if (selectedTemplateName !== "") {
      const selectedTemplate = apiData?.results.find(
        (item) => item.template_name === selectedTemplateName,
      );

      if (selectedTemplate) {
        setTemplatePreviewData(selectedTemplate.template_preview_json);
        setTemplateData(selectedTemplate.template_json);
        setMediaType("none");
        updateRequiredFields(
          selectedTemplateName,
          selectedTemplate.template_preview_json,
        );
      }
    }
  };

  const resetForm = () => {
    setFormValues((prevValues) => {
      const persistentValues = {};
      return persistentValues;
    });
    setRequiredFieldsToRender([]);
    setMediaType("none");
    setMediaFile(null);
    setMediaFileError(false);
    setCarouselFileErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setCarouselMediaFiles({});
  };

  const handleBroadcastTypeChange = (event) => {
    setBroadcastType(event.target.value);
    if (event.target.value === "now") {
      setCsvFile(null);
      setCountryCode("");
      setDialCode("");
      setName("");
      // Don't reset campaignName and campaignDescription as they come from modal
      setSelectedSegment(null);
      setSelectedSegmentList([]);
      setPhoneNumberCount(0);
    }
    if (event.target.value !== "one-time") {
      setScheduleDate("");
      setBroadcastTime("");
    }
  };

  const handleCancelSchedule = () => {
    setBroadcastType("now");
    setScheduleDate("");
    setBroadcastTime("");
    setCountryCode("");
    setDialCode("");
    setName("");
    // Don't reset campaignName and campaignDescription
    setSelectedSegment(null);
    setSelectedSegmentList([]);
    setPhoneNumberCount(0);
  };

  function formatReadableDate(isoString) {
    const date = new Date(isoString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";
    return `${day}${suffix} ${month} ${year}`;
  }

  const filteredAndSortedTemplates = useMemo(() => {
    if (categoryFilter === "retry") return [];

    let filtered = templateOptions.filter((template) => {
      const nameMatch = template?.template_name
        ?.toLowerCase()
        ?.includes(searchQuery.toLowerCase());
      const contentMatch = template?.template_json?.content
        ?.toLowerCase()
        ?.includes(searchQuery.toLowerCase());
      return nameMatch || contentMatch;
    });

    if (categoryFilter) {
      filtered = filtered.filter(
        (template) =>
          template.category.toLowerCase() === categoryFilter.toLowerCase(),
      );
    }

    filtered.sort((a, b) => {
      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();
      switch (sortBy) {
        case "date-desc":
          return dateB - dateA;
        case "date-asc":
          return dateA - dateB;
        case "name-asc":
          return a.template_name.localeCompare(b.template_name);
        case "name-desc":
          return b.template_name.localeCompare(a.template_name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [templateOptions, searchQuery, sortBy, categoryFilter]);

  const updateRequiredFields = (templateName, selectedTemplatePreviewJson) => {
    const requiredFieldsForTemplate = requiredFields[templateName];
    if (requiredFieldsForTemplate) {
      const fieldsToRender = [];
      const isCarousel = requiredFieldsForTemplate.some(
        (field) =>
          field.startsWith("cardimage") ||
          field.startsWith("cardvideo") ||
          field.startsWith("cardbody"),
      );
      const updatedFormValues = { ...formValues };

      const getCardIndex = (fieldName) => {
        const matches = fieldName.match(/_(\d+)$/);
        return matches ? parseInt(matches[1]) : null;
      };

      const cardParams = {};
      if (isCarousel) {
        requiredFieldsForTemplate.forEach((field) => {
          const cardIndex = getCardIndex(field);
          if (cardIndex !== null) {
            if (!cardParams[cardIndex]) {
              cardParams[cardIndex] = [];
            }
            cardParams[cardIndex].push(field);
          }
        });
      }

      const fieldHandlers = {
        hparameter: () => {
          const paramType =
            formValues.param_types?.hparameter?.param_type || "custom";
          fieldsToRender.push(
            <Grid
              container
              direction="row"
              spacing={2}
              key="hparameter"
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="header-param-label">
                    Header Parameter Type
                  </InputLabel>
                  <Select
                    name="hparameter_select"
                    value={paramType}
                    labelId="header-param-label"
                    label="Header Parameter Type"
                    onChange={(e) =>
                      handleSelectParamTypeChange("hparameter", e.target.value)
                    }
                  >
                    {selectOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {paramType === "custom" && (
                  <TextField
                    fullWidth
                    label="Enter Value"
                    name="hparameter"
                    onChange={handleInputChange}
                    required
                  />
                )}
              </Grid>
            </Grid>,
          );
        },
        bparameter: () => {
          const bparameterCount = requiredFieldsForTemplate.filter(
            (field) => field === "bparameter",
          ).length;
          for (let i = 0; i < bparameterCount; i++) {
            const fieldName = `bparameter_${i}`;
            const paramType =
              updatedFormValues.param_types?.[fieldName]?.param_type || "";
            fieldsToRender.push(
              <Grid
                container
                direction="row"
                spacing={2}
                key={fieldName}
                sx={{ marginBottom: 2 }}
              >
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel
                      id={`main-body-param-label-${i + 1}`}
                    >{`Main Body Parameter ${i + 1}`}</InputLabel>
                    <Select
                      name={`${fieldName}_select`}
                      labelId={`main-body-param-label-${i + 1}`}
                      label={`Main Body Parameter ${i + 1}`}
                      onChange={(e) =>
                        handleSelectParamTypeChange(fieldName, e.target.value)
                      }
                    >
                      {selectOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  {paramType === "custom" && (
                    <TextField
                      fullWidth
                      label={`${paramType}`}
                      name={fieldName}
                      onChange={handleInputChange}
                    />
                  )}
                </Grid>
              </Grid>,
            );
          }
        },
        urlparameter: () => {
          const urlParamterCount = requiredFieldsForTemplate.filter(
            (field) => field === "urlparameter",
          ).length;
          for (let i = 0; i < urlParamterCount; i++) {
            fieldsToRender.push(
              <Grid
                container
                direction="column"
                key={`urlparameter_${i}`}
                sx={{ marginBottom: 2, width: { xs: "100%" } }}
              >
                <TextField
                  key={`urlparameter_${i}`}
                  label="URL Parameter"
                  name={`urlparameter_${i}`}
                  onChange={handleInputChange}
                  required
                />
              </Grid>,
            );
          }
          updatedFormValues.urlparameter = "";
        },
        imageparameter: () => {
          setMediaType("image");
          fieldsToRender.push(getFileInput("image"));
          updatedFormValues.imageparameter = "";
        },
        videoparameter: () => {
          setMediaType("video");
          fieldsToRender.push(getFileInput("video"));
          updatedFormValues.videoparameter = "";
        },
        documentparameter: () => {
          setMediaType("document");
          fieldsToRender.push(getFileInput("document"));
          updatedFormValues.documentparameter = "";
        },
        gifparameter: () => {
          setMediaType("gif");
          fieldsToRender.push(getFileInput("gif"));
          updatedFormValues.gifparameter = "";
        },
        locationparameter: () => {
          fieldsToRender.push(
            <>
              <Grid
                container
                direction="column"
                key="locationparameter_name"
                sx={{ marginBottom: 2, width: { xs: "100%" } }}
              >
                <TextField
                  key="locationparameter_name"
                  label="Location Name"
                  name="locationparameter_name"
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid
                container
                direction="column"
                key="locationparameter_address"
                sx={{ marginBottom: 2, width: { xs: "100%" } }}
              >
                <TextField
                  key="locationparameter_address"
                  label="Location Address"
                  name="locationparameter_address"
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid
                container
                direction="column"
                key="locationparameter_latitude"
                sx={{ marginBottom: 2, width: { xs: "100%" } }}
              >
                <TextField
                  key="locationparameter_latitude"
                  type="number"
                  inputProps={{ step: "0.000001", min: -90, max: 90 }}
                  label="Location Latitude"
                  name="locationparameter_latitude"
                  onChange={handleInputChange}
                  required
                  error={formErrors.latitude}
                  helperText={
                    formErrors.latitude
                      ? "Latitude must be between -90 and 90"
                      : ""
                  }
                />
              </Grid>
              <Grid
                container
                direction="column"
                key="locationparameter_longitude"
                sx={{ marginBottom: 2, width: { xs: "100%" } }}
              >
                <TextField
                  key="locationparameter_longitude"
                  type="number"
                  inputProps={{ step: "0.000001", min: -180, max: 180 }}
                  label="Location Longitude"
                  name="locationparameter_longitude"
                  onChange={handleInputChange}
                  required
                  error={formErrors.longitude}
                  helperText={
                    formErrors.longitude
                      ? "Longitude must be between -180 and 180"
                      : ""
                  }
                />
              </Grid>
            </>,
          );
          const placeholderLocationField = selectedTemplatePreviewJson?.location
            ? JSON.parse(selectedTemplatePreviewJson.location)
            : {};
          updatedFormValues.locationparameter_name =
            placeholderLocationField?.name || "";
          updatedFormValues.locationparameter_address =
            placeholderLocationField?.address || "";
          updatedFormValues.locationparameter_latitude =
            placeholderLocationField?.latitude || "";
          updatedFormValues.locationparameter_longitude =
            placeholderLocationField?.longitude || "";
        },
        cardimageparameter: (cardIndex) => {
          const fieldName = `cardimageparameter_${cardIndex}`;
          fieldsToRender.push(
            <Grid
              container
              direction="column"
              key={`${fieldName}-${JSON.stringify(carouselFileErrors)}`}
              sx={{ marginBottom: 2 }}
            >
              <Typography variant="subtitle1">
                Card {cardIndex + 1} Image
              </Typography>
              {getCarouselFileInput("image", cardIndex)}
              <CarouselFileDisplay
                file={carouselMediaFiles[cardIndex]}
                cardIndex={cardIndex}
                onDelete={handleDeleteCarouselMediaFile}
                fileError={carouselFileErrors[cardIndex]}
              />
            </Grid>,
          );
          updatedFormValues[fieldName] = "";
        },
        cardvideoparameter: (cardIndex) => {
          const fieldName = `cardvideoparameter_${cardIndex}`;
          fieldsToRender.push(
            <Grid
              container
              direction="column"
              key={`${fieldName}-${JSON.stringify(carouselFileErrors)}`}
              sx={{ marginBottom: 2 }}
            >
              <Typography variant="subtitle1">
                Card {cardIndex + 1} Video
              </Typography>
              {getCarouselFileInput("video", cardIndex)}
              <CarouselFileDisplay
                file={carouselMediaFiles[cardIndex]}
                cardIndex={cardIndex}
                onDelete={handleDeleteCarouselMediaFile}
                fileError={carouselFileErrors[cardIndex]}
              />
            </Grid>,
          );
          updatedFormValues[fieldName] = "";
        },
        cardbodyparameter: (cardIndex, cardFieldIndex) => {
          const fieldName = `cardbodyparameter_${cardIndex}_${cardFieldIndex}`;
          fieldsToRender.push(
            <Grid
              container
              direction="column"
              key={fieldName}
              sx={{ marginBottom: 2, width: { xs: "100%" } }}
            >
              <TextField
                key={fieldName}
                label={`Card ${parseInt(cardIndex) + 1} Body Parameter ${cardFieldIndex}`}
                name={fieldName}
                onChange={handleInputChange}
                required
              />
            </Grid>,
          );
          const placeholderCardField =
            selectedTemplatePreviewJson?.carouselCards
              ? JSON.parse(selectedTemplatePreviewJson.carouselCards)[cardIndex]
              : "";
          updatedFormValues[fieldName] =
            placeholderCardField?.sampleBodyFields[cardFieldIndex - 1]?.value ||
            "";
        },
        cardurlparameter: (cardIndex) => {
          const fieldName = `cardurlparameter_${cardIndex}`;
          fieldsToRender.push(
            <Grid
              container
              direction="column"
              key={fieldName}
              sx={{ marginBottom: 2, width: { xs: "100%" } }}
            >
              <TextField
                key={fieldName}
                label={`Card ${cardIndex + 1} URL Parameter`}
                name={fieldName}
                onChange={handleInputChange}
                required
              />
            </Grid>,
          );
          updatedFormValues[fieldName] = "";
        },
      };

      let standardFields = requiredFieldsForTemplate.filter(
        (field) =>
          !field.startsWith("cardimage") &&
          !field.startsWith("cardvideo") &&
          !field.startsWith("cardbody") &&
          !field.startsWith("cardurl"),
      );
      standardFields = standardFields.filter(
        (value, index, array) =>
          value !== "bparameter" || array.indexOf(value) === index,
      );
      standardFields = standardFields.filter(
        (value, index, array) =>
          value !== "urlparameter" || array.indexOf(value) === index,
      );
      standardFields = standardFields.filter(
        (value, index, array) =>
          value !== "cardbodyparameter" || array.indexOf(value) === index,
      );
      const moveToStart = (arr, value) => {
        if (arr.includes(value)) {
          return [value, ...arr.filter((v) => v !== value)];
        }
        return arr;
      };
      standardFields = moveToStart(standardFields, "hparameter");

      standardFields.forEach((field) => {
        if (fieldHandlers[field]) fieldHandlers[field]();
      });

      if (isCarousel) {
        fieldsToRender.push(
          <Typography
            variant="h6"
            key="carousel-header"
            sx={{ marginTop: 2, marginBottom: 1 }}
          >
            Carousel Cards Parameters
          </Typography>,
        );
        Object.keys(cardParams)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .forEach((cardIndex) => {
            const cardFields = cardParams[cardIndex];
            fieldsToRender.push(
              <>
                <Divider
                  key={`divider-${cardIndex}`}
                  sx={{ width: "100%", my: 2 }}
                />
              </>,
            );
            fieldsToRender.push(
              <Typography
                variant="h6"
                key={`card-title-${cardIndex}`}
                sx={{ marginBottom: 1 }}
              >
                Card {parseInt(cardIndex) + 1}
              </Typography>,
            );
            cardFields.forEach((field, cardFieldIndex) => {
              if (field.startsWith("cardimageparameter")) {
                fieldHandlers.cardimageparameter(parseInt(cardIndex));
              } else if (field.startsWith("cardvideoparameter")) {
                fieldHandlers.cardvideoparameter(parseInt(cardIndex));
              } else if (field.startsWith("cardbodyparameter")) {
                fieldHandlers.cardbodyparameter(
                  parseInt(cardIndex),
                  cardFieldIndex,
                );
              } else if (field.startsWith("cardurlparameter")) {
                fieldHandlers.cardurlparameter(parseInt(cardIndex));
              }
            });
          });
      }

      const paramTypesInit = { ...(formValues.param_types || {}) };
      requiredFieldsForTemplate.forEach((field, idx) => {
        const placeholderHeaderFields =
          selectedTemplatePreviewJson?.sampleHeaderFields || [];
        const placeholderBodyFields = isCarousel
          ? selectedTemplatePreviewJson?.carouselMainBodySampleFields || []
          : selectedTemplatePreviewJson?.sampleBodyFields || [];

        // An entry with no param_type was made before this template's preview
        // JSON had loaded, so fill it now (a type the user picked is kept).
        if (field === "bparameter") {
          const fname = `bparameter_${idx}`;
          if (!paramTypesInit[fname]?.param_type) {
            paramTypesInit[fname] = {
              name: fname,
              param_type: placeholderBodyFields[idx]?.type || "",
              value: placeholderBodyFields[idx]?.value || "",
            };
          }
        }

        if (field === "hparameter") {
          if (!paramTypesInit.hparameter?.param_type) {
            paramTypesInit.hparameter = {
              name: "hparameter",
              param_type: placeholderHeaderFields[0]?.type || "",
              value: placeholderHeaderFields[0]?.value || "",
            };
          }
        }
      });

      requiredFieldsForTemplate
        .filter((field) => field === "urlparameter")
        .forEach((field, idx) => {
          const placeholderURLFields =
            selectedTemplatePreviewJson?.allButtons?.map(
              (button) => button?.data?.sampleUrl,
            ) || [];
          if (field === "urlparameter") {
            const fname = `urlparameter_${idx}`;
            if (!paramTypesInit[fname]) {
              paramTypesInit[fname] = {
                name: fname,
                param_type: "custom" || "",
                value: placeholderURLFields[idx] || "",
              };
            }
          }
        });

      updatedFormValues.param_types = paramTypesInit;
      setFormValues((prevValues) => ({
        ...prevValues,
        ...updatedFormValues,
      }));
      setRequiredFieldsToRender(fieldsToRender);
    } else {
      resetForm();
    }
  };

  const handleSelectParamTypeChange = (fieldName, value) => {
    setFormValues((prev) => {
      const updatedParamTypes = {
        ...(prev.param_types || {}),
        [fieldName]: {
          name: fieldName,
          param_type: value,
          value: value === "custom" ? "" : value,
        },
      };
      return {
        ...prev,
        [fieldName]: updatedParamTypes[fieldName].value,
        param_types: updatedParamTypes,
      };
    });

    setRequiredFieldsToRender((prevFields) => {
      return prevFields.map((field) => {
        if (
          field.key &&
          field.key.startsWith("bparameter_") &&
          field.key === fieldName
        ) {
          const paramType = value;
          const idx = parseInt(fieldName.split("_")[1]);
          return (
            <Grid
              container
              direction="row"
              spacing={2}
              key={fieldName}
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel
                    id={`main-body-param-label-${idx}`}
                  >{`Main Body Parameter ${idx + 1}`}</InputLabel>
                  <Select
                    name={`${fieldName}_select`}
                    labelId={`main-body-param-label-${idx}`}
                    label={`Main Body Parameter ${idx + 1}`}
                    value={value}
                    onChange={(e) =>
                      handleSelectParamTypeChange(fieldName, e.target.value)
                    }
                  >
                    {selectOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {paramType === "custom" && (
                  <TextField
                    fullWidth
                    label="Value"
                    name={fieldName}
                    onChange={handleInputChange}
                  />
                )}
              </Grid>
            </Grid>
          );
        }
        if (field.key === "hparameter" && field.key === fieldName) {
          const paramType = value;
          return (
            <Grid
              container
              direction="row"
              spacing={2}
              key="hparameter"
              sx={{ marginBottom: 2 }}
            >
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="header-param-label">
                    Header Parameter Type
                  </InputLabel>
                  <Select
                    name="hparameter_select"
                    value={value}
                    labelId="header-param-label"
                    label="Header Parameter Type"
                    onChange={(e) =>
                      handleSelectParamTypeChange("hparameter", e.target.value)
                    }
                  >
                    {selectOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {paramType === "custom" && (
                  <TextField
                    fullWidth
                    label="Value"
                    name="hparameter"
                    onChange={handleInputChange}
                  />
                )}
              </Grid>
            </Grid>
          );
        }
        return field;
      });
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => {
      const paramType = prev.param_types?.[name]?.param_type || "custom";
      return {
        ...prev,
        [name]: value,
        param_types: {
          ...(prev.param_types || {}),
          [name]: {
            name: name,
            param_type: paramType,
            value: value,
          },
        },
      };
    });
  };

  const validateWalletForSegment = async ({
    token,
    tenant,
    segmentId,
    filterQueryParams,
  }) => {
    try {
      const res = await axios.post(
        VALIDATE_WALLET_ENDPOINT,
        {
          campaign_option: "option2",
          segment_id: segmentId,
          filter_query_params: filterQueryParams,
          category: "marketing",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );
      setWalletValidation(res.data);
      setWalletErrorAcknowledged(false);
      return true;
    } catch (err) {
      setWalletValidation(err.response?.data);
      setWalletErrorAcknowledged(false);
      return false;
    }
  };

  const getMinimumScheduleTime = () => {
    if (!isRetryEdit) {
      return new Date();
    }
    const now = new Date();
    const originalScheduledTime = props?.pageParams?.broadcast
      ?.scheduled_datetime
      ? new Date(props.pageParams.broadcast.scheduled_datetime)
      : null;
    const twentyFourHoursFromNow = new Date(
      now.getTime() + 24 * 60 * 60 * 1000,
    );
    if (
      originalScheduledTime &&
      originalScheduledTime < twentyFourHoursFromNow
    ) {
      return originalScheduledTime;
    }
    return twentyFourHoursFromNow;
  };

  const isScheduleInFuture = () => {
    if (!scheduleDate || !broadcastTime) return true;
    const scheduled = new Date(scheduleDate);
    scheduled.setHours(
      broadcastTime.getHours(),
      broadcastTime.getMinutes(),
      0,
      0,
    );
    const minTime = getMinimumScheduleTime();
    return scheduled >= minTime;
  };

  const validateScheduleTime = () => {
    if (!scheduleDate || !broadcastTime) {
      setScheduleError("");
      return true;
    }
    const scheduled = new Date(scheduleDate);
    scheduled.setHours(
      broadcastTime.getHours(),
      broadcastTime.getMinutes(),
      0,
      0,
    );
    const minTime = getMinimumScheduleTime();
    if (scheduled < minTime) {
      if (isRetryEdit) {
        const originalScheduledTime = props?.pageParams?.broadcast
          ?.scheduled_datetime
          ? new Date(props.pageParams.broadcast.scheduled_datetime)
          : null;
        const twentyFourHoursFromNow = new Date(
          Date.now() + 24 * 60 * 60 * 1000,
        );
        if (
          originalScheduledTime &&
          originalScheduledTime < twentyFourHoursFromNow
        ) {
          setScheduleError(
            `Please select a time at or after the original scheduled time: ${format(originalScheduledTime, "MMM dd, yyyy HH:mm")}`,
          );
        } else {
          setScheduleError(
            `Please select a time at least 24 hours from now: ${format(twentyFourHoursFromNow, "MMM dd, yyyy HH:mm")}`,
          );
        }
      } else {
        setScheduleError("Please select a future date and time");
      }
      return false;
    }
    setScheduleError("");
    return true;
  };

  const handleEditScheduledBroadcast = async ({ token, tenant }) => {
    if (!validateScheduleTime()) {
      return;
    }
    setScheduleError();

    const broadcastLogId = props?.pageParams?.broadcast?.id;
    if (!broadcastLogId) {
      throw new Error("Missing broadcast log id");
    }

    let segmentChanged = false;
    const payload = new FormData();

    if (isRetryEdit) {
      payload.append("isRetryEdit", true);
    }

    if (selectedTemplate !== props?.pageParams.templateName) {
      payload.append("template_name", selectedTemplate);
    }

    if (dialCode && selectedCampaign === "option3") {
      payload.append("phoneNumber", dialCode);
      payload.append("countryCode", countryCode);
    }

    if (selectedCampaign === "option1" && csvFile && csvFile instanceof File) {
      payload.append("csv_file", csvFile);
      payload.append("campaign_option", "option1");
      try {
        const csvFileBuffer = await csvFile.arrayBuffer();
        const walletPayload = new FormData();
        walletPayload.append("template_json", JSON.stringify(templateData));
        walletPayload.append("campaign_option", "option1");
        walletPayload.append(
          "campaign_name",
          campaignName || "Edit CSV Broadcast",
        );
        walletPayload.append(
          "csv_file",
          new File([csvFileBuffer], csvFile.name, {
            type: csvFile.type,
            lastModified: csvFile.lastModified,
          }),
        );
        if (
          formValues.param_types &&
          Object.keys(formValues.param_types).length > 0
        ) {
          walletPayload.append(
            "param_types",
            JSON.stringify(formValues.param_types),
          );
        }
        const walletResponse = await axios.post(
          VALIDATE_WALLET_ENDPOINT,
          walletPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-Id": tenant,
            },
          },
        );
      } catch (error) {
        setWalletErrorDialog({
          open: true,
          data: error.response?.data,
        });
        setLoading(false);
        return;
      }
    }

    if (
      selectedCampaign === "option2" &&
      selectedSegment?.id &&
      selectedSegment.name !== props?.pageParams.segmentName
    ) {
      payload.append("segment_id", selectedSegment.id);
      payload.append(
        "filter_query_params",
        convertFiltersToQueryParams(selectedSegment.filters),
      );
      segmentChanged = true;
    }

    if (scheduleDate && broadcastTime) {
      payload.append("scheduled_date", format(scheduleDate, "yyyy-MM-dd"));
      payload.append("scheduled_time", format(broadcastTime, "HH:mm"));
    }

    if (![...payload.entries()].length) {
      setAlertObj({ active: true, type: "info", msg: "No changes to save" });
      setLoading(false);
      return;
    }

    if (segmentChanged) {
      const walletOk = await validateWalletForSegment({
        token,
        tenant,
        segmentId: selectedSegment.id,
        filterQueryParams: convertFiltersToQueryParams(selectedSegment.filters),
      });
      if (!walletOk) {
        setLoading(false);
        return;
      }
    }

    try {
      const response = await axios.put(
        `${EDIT_SCHEDULED_BROADCAST}${broadcastLogId}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );
      const audienceCount = response.data.audience_count;
      const successMsg = audienceCount
        ? `Scheduled broadcast updated successfully with ${audienceCount} recipients`
        : "Scheduled broadcast updated successfully";
      setAlertObj({ active: true, type: "success", msg: successMsg });
      setSuccessResponse(true);
      singlePage?.onSubmitted?.();
      setTimeout(() => {
        goToBroadcastList();
      }, 2000);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        setCSVError({
          summary: errorData.error_summary || "CSV validation failed",
          details: errorData.errors,
        });
        setCsvErrorAcknowledged(false);
        if (csvInputRef.current) {
          csvInputRef.current.resetCsv();
        }
      } else {
        setAlertObj({
          active: true,
          type: "error",
          msg: errorData?.detail || "Failed to update broadcast",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    setLoading(true);
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    if (event) event.preventDefault();

    if (isEditMode) {
      const res = await handleEditScheduledBroadcast({ token, tenant });
      setLoading(false);
      return;
    }

    let csvFileBuffer = null;
    let mediaFileBuffer = null;
    let carouselFileBuffers = {};

    try {
      if (csvFile && csvFile instanceof File) {
        csvFileBuffer = await csvFile.arrayBuffer();
      }
      if (mediaFile && mediaFile instanceof File) {
        mediaFileBuffer = await mediaFile.arrayBuffer();
      }
      if (carouselMediaFiles && typeof carouselMediaFiles === "object") {
        for (const [index, file] of Object.entries(carouselMediaFiles)) {
          if (file && file instanceof File) {
            carouselFileBuffers[index] = await file.arrayBuffer();
          }
        }
      }
    } catch (error) {
      setLoading(false);
      setAlertObj({
        active: true,
        type: "error",
        msg: `Failed to read file(s): ${error.message}`,
      });
      return;
    }

    const buildPayload = () => {
      const payload = new FormData();
      payload.append("template_json", JSON.stringify(templateData));

      Object.keys(formValues).forEach((key) => {
        if (
          formValues[key] === undefined ||
          formValues[key] === null ||
          formValues[key] === ""
        ) {
          return;
        }
        if (key.startsWith("bparameter_")) {
          payload.append(key, formValues[key]);
        } else if (
          key.startsWith("cardbodyparameter_") ||
          key.startsWith("cardurlparameter_")
        ) {
          const match = key.match(/_(\d+)(?:_\d+)?$/);
          if (match) {
            payload.append(key, formValues[key]);
          }
        } else {
          payload.append(key, formValues[key]);
        }
      });

      if (
        formValues.param_types &&
        Object.keys(formValues.param_types).length > 0
      ) {
        payload.append("param_types", JSON.stringify(formValues.param_types));
      }

      if (mediaFileBuffer) {
        payload.append(
          "file",
          new File([mediaFileBuffer], mediaFile.name, {
            type: mediaFile.type,
            lastModified: mediaFile.lastModified,
          }),
        );
      }

      if (Object.keys(carouselFileBuffers).length > 0) {
        Object.entries(carouselFileBuffers).forEach(([cardIndex, buffer]) => {
          const originalFile = carouselMediaFiles[cardIndex];
          payload.append(
            `carousel_file_${cardIndex}`,
            new File([buffer], originalFile.name, {
              type: originalFile.type,
              lastModified: originalFile.lastModified,
            }),
          );
        });
      }

      if (categoryFilter) {
        payload.append("category", categoryFilter);
      }

      return payload;
    };

    let apiResponse = null;

    if (selectedCampaign === "option1") {
      if (campaignName && csvFileBuffer) {
        const payload = buildPayload();
        payload.append("campaign_option", selectedCampaign);
        payload.append("campaign_name", campaignName);
        payload.append("campaign_description", campaignDescription);
        payload.append(
          "csv_file",
          new File([csvFileBuffer], csvFile.name, {
            type: csvFile.type,
            lastModified: csvFile.lastModified,
          }),
        );

        try {
          apiResponse = await axios.post(VALIDATE_WALLET_ENDPOINT, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-Id": tenant,
            },
          });
        } catch (error) {
          const errorData = error.response?.data;

          if (errorData?.status === "error" && errorData?.errors?.file) {
            let summary = "CSV validation failed.";
            let details = [];
            const fileErrors = errorData.errors.file;
            if (Array.isArray(fileErrors)) {
              summary = "CSV file validation failed.";
              details = fileErrors;
            } else if (typeof fileErrors === "object") {
              summary =
                fileErrors.error_summary?.string ||
                fileErrors.error_summary ||
                "CSV validation failed.";
              if (Array.isArray(fileErrors.errors)) {
                details = fileErrors.errors.map((err) => err?.string || err);
              }
            }
            setCSVError({ summary, details });
            setCsvErrorAcknowledged(false);
            if (csvInputRef.current?.resetCsv) {
              csvInputRef.current.resetCsv();
            }
            setLoading(false);
            return;
          }

          if (
            errorData?.status === "error" &&
            (errorData?.current_balance !== undefined ||
              errorData?.message?.includes("wallet balance"))
          ) {
            setWalletErrorDialog({ open: true, data: errorData });
            setLoading(false);
            return;
          }

          setAlertObj({
            active: true,
            type: "error",
            msg: errorData?.message || "Validation failed. Please try again.",
          });
          setLoading(false);
          return;
        }
      } else {
        if (!csvFileBuffer) {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Please upload a CSV file",
          });
          setLoading(false);
          return;
        }
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please enter all the required fields",
        });
        setLoading(false);
        return;
      }
    } else if (selectedCampaign === "option2") {
      if (campaignName && selectedSegment && phoneNumberCount > 0) {
        const payload = buildPayload();
        payload.append("campaign_option", selectedCampaign);
        payload.append("campaign_name", campaignName);
        payload.append("campaign_description", campaignDescription);
        payload.append("segment_id", selectedSegment.id);
        payload.append(
          "filter_query_params",
          convertFiltersToQueryParams(selectedSegment.filters),
        );
        try {
          apiResponse = await axios.post(VALIDATE_WALLET_ENDPOINT, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-Id": tenant,
            },
          });
        } catch (error) {
          const walletData = error.response?.data;
          setWalletErrorDialog({ open: true, data: walletData });
          setLoading(false);
          return;
        }
      } else {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please enter all the required fields",
        });
        setLoading(false);
        return;
      }
    } else if (selectedCampaign === "option3") {
      if (countryCode && dialCode && name) {
        const payload = buildPayload();
        payload.append("campaign_option", selectedCampaign);
        payload.append("country_code", countryCode);
        payload.append("dial_code", dialCode);
        payload.append("name", name);

        try {
          apiResponse = await axios.post(VALIDATE_WALLET_ENDPOINT, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-Id": tenant,
            },
          });
        } catch (error) {
          const walletData = error.response?.data;
          setWalletErrorDialog({ open: true, data: walletData });
          setLoading(false);
          return;
        }
      } else {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please enter all required fields including name and phone number",
        });
        setLoading(false);
        return;
      }
    }

    const broadcastPayload = buildPayload();
    // Campaigns (Beta): add to an existing campaign and name the broadcast
    if (campaignsV2Params.existingCampaignId) {
      broadcastPayload.append("existing_campaign_id", campaignsV2Params.existingCampaignId);
    }
    if (campaignsV2Params.broadcastName) {
      broadcastPayload.append("broadcast_name", campaignsV2Params.broadcastName);
    }
    if (campaignsV2Params.broadcastDescription) {
      broadcastPayload.append("broadcast_description", campaignsV2Params.broadcastDescription);
    }
    if (enableTimeSlot) {
      broadcastPayload.append("smartRetryEnabled", enableTimeSlot);
      broadcastPayload.append("timeSlot", timeSlot);
    }
    if (singlePage && isEnableCheckBoxVisible) {
      Object.entries(fallbackFields(fallback, "whatsapp")).forEach(([key, value]) => broadcastPayload.append(key, value));
    }

    if (selectedCampaign === "option1") {
      broadcastPayload.append("campaign_option", selectedCampaign);
      broadcastPayload.append("campaign_name", campaignName);
      broadcastPayload.append("campaign_description", campaignDescription);
      broadcastPayload.append(
        "csv_file",
        new File([csvFileBuffer], csvFile.name, {
          type: csvFile.type,
          lastModified: csvFile.lastModified,
        }),
      );
    } else if (selectedCampaign === "option2") {
      broadcastPayload.append("campaign_option", selectedCampaign);
      broadcastPayload.append("campaign_name", campaignName);
      broadcastPayload.append("campaign_description", campaignDescription);
      broadcastPayload.append("segment_id", selectedSegment.id);
      broadcastPayload.append(
        "filter_query_params",
        convertFiltersToQueryParams(selectedSegment.filters),
      );
    } else if (selectedCampaign === "option3") {
      broadcastPayload.append("campaign_option", selectedCampaign);
      broadcastPayload.append("country_code", countryCode);
      broadcastPayload.append("dial_code", dialCode);
      broadcastPayload.append("name", name);
    }

    if (broadcastType === "one-time" || broadcastType === "recurring") {
      broadcastPayload.append("broadcast_type", broadcastType);

      if (broadcastType === "one-time") {
        const localDate = new Date(scheduleDate);
        const localTime = new Date(broadcastTime).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        const formattedDate = localDate.toLocaleDateString("en-CA");
        broadcastPayload.append("schedule_date", formattedDate);
        broadcastPayload.append("broadcast_time", localTime);
      }

      if (broadcastType === "recurring") {
        broadcastPayload.append(
          "repeat_interval",
          recurringData.repeatInterval,
        );
        broadcastPayload.append(
          "monthly_pattern",
          recurringData.monthlyPattern,
        );
        if (recurringData.monthlyPattern === "dayOfMonth") {
          broadcastPayload.append("day_of_month", recurringData.dayOfMonth);
        } else {
          broadcastPayload.append("week_of_month", recurringData.weekOfMonth);
          broadcastPayload.append("day_of_week", recurringData.dayOfWeek);
        }
        broadcastPayload.append(
          "weekend_handling",
          recurringData.weekendHandling,
        );
        if (recurringData.startRecurrence) {
          const startDate = new Date(recurringData.startRecurrence);
          broadcastPayload.append(
            "start_recurrence",
            startDate.toLocaleDateString("en-CA"),
          );
        }
        if (!recurringData.noEndDate && recurringData.endRecurrence) {
          const endDate = new Date(recurringData.endRecurrence);
          broadcastPayload.append(
            "end_recurrence",
            endDate.toLocaleDateString("en-CA"),
          );
        }
        broadcastPayload.append(
          "no_end_date",
          recurringData.noEndDate ? true : false,
        );
        if (broadcastTime) {
          const time = new Date(broadcastTime);
          const formattedTime = time.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });
          broadcastPayload.append("broadcast_time", formattedTime);
        }
      }

      broadcastPayload.append("notify_before", notifyBefore ? true : false);
      broadcastPayload.append("notify_status", notifyStatus ? true : false);
    }

    let slowProcessTimer;
    let hasNavigated = false;

    try {
      const BROADCAST_TIMEOUT_SECONDS = 45;

      const navigateToCampaignManagement = () => {
        if (hasNavigated) return;
        hasNavigated = true;
        singlePage?.onSubmitted?.(); // request is still processing on the server: leaving is expected
        if (broadcastType === "one-time" || broadcastType === "recurring") {
          goToBroadcastList({ tab: 1 });
        } else {
          goToBroadcastList();
        }
      };

      slowProcessTimer = setTimeout(() => {
        setIsSlowProcessing(true);
        setTimeout(() => {
          navigateToCampaignManagement();
        }, 3000);
      }, BROADCAST_TIMEOUT_SECONDS * 1000);

      const broadcastApiResponse = await axios.post(
        TEMPLATE_BROADCAST_ENDPOINT,
        broadcastPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );

      clearTimeout(slowProcessTimer);
      setIsSlowProcessing(false);

      // CSV failures can come back as 200 with status "error"; handle them like an error response
      if (
        selectedCampaign === "option1" &&
        broadcastApiResponse.data?.status === "error"
      ) {
        throw Object.assign(new Error("CSV broadcast failed"), {
          response: broadcastApiResponse,
        });
      }

      setSuccessResponse(true);
      singlePage?.onSubmitted?.();

      if (broadcastType === "recurring") {
        setAlertObj({
          active: true,
          type: "success",
          msg: "Broadcast has been scheduled successfully",
        });
      } else if (broadcastType === "one-time") {
        setAlertObj({
          active: true,
          type: "success",
          msg: "Broadcast submitted successfully. An email has been sent with all the detail.",
        });
      } else {
        setAlertObj({
          active: true,
          type: "success",
          msg: "Broadcast has been created successfully",
        });
      }

      setTimeout(() => {
        if (broadcastType === "one-time" || broadcastType === "recurring") {
          goToBroadcastList({ tab: 1 });
        } else {
          goToBroadcastList();
        }
      }, 2500);
    } catch (error) {
      const data = error?.response?.data;
      clearTimeout(slowProcessTimer);
      setIsSlowProcessing(false);
      setIsSubmitting(false);

      if (
        data?.status === "error" &&
        data?.campaign_type === "CSV" &&
        data?.errors?.file
      ) {
        let summary = "CSV validation failed.";
        let details = [];
        if (Array.isArray(data.errors.file)) {
          summary = "CSV file validation failed.";
          details = data.errors.file;
        } else if (typeof data.errors.file === "object") {
          summary = data.errors.file.error_summary || "CSV validation failed.";
          details = Array.isArray(data.errors.file.errors)
            ? data.errors.file.errors
            : [];
        }
        setCSVError({ summary, details });
        setCsvErrorAcknowledged(false);
        csvInputRef.current?.resetCsv();
        return;
      }

      setAlertObj({
        active: true,
        type: "error",
        msg: `Broadcast Failed - ${error?.response?.status === 401 ? "Unauthorized" : data?.message || "Unexpected server error"}`,
      });
    } finally {
      setLoading(false);
      clearTimeout(slowProcessTimer);
      setIsSlowProcessing(false);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    handleBroadcastTypeChange({ target: { value: broadcastType } });
  }, [broadcastType]);

  // Template step check: header/body variables filled and the inputs pass the browser's form validation
  const validateTemplateFields = (formElement) => {
    if ("hparameter" in formValues && formValues.hparameter.trim() === "") {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please fill the header parameter",
      });
      return false;
    }

    const bparameterFields = Object.keys(formValues?.param_types || {}).filter(
      (key) => key.startsWith("bparameter_"),
    );
    if (
      bparameterFields?.some(
        (key) => formValues?.param_types[key].value?.trim() === "",
      )
    ) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please fill the body parameters",
      });
      return false;
    }

    if (formElement && !formElement.checkValidity()) {
      formElement.reportValidity();
      return false;
    }
    return true;
  };

  const handleCampaignClick = (event) => {
    event.preventDefault();
    if (validateTemplateFields(event.target)) {
      setShowCampaignOptions(true);
      setActiveStep(1);
    }
  };

  // Audience step check: the chosen option (CSV / segment / single contact) has what it needs
  const validateAudience = () => {
      if (
        selectedCampaign === "option3" &&
        (countryCode === "" || dialCode === "" || name === "")
      ) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please enter all required fields including name and phone number",
        });
        setLoading(false);
        setStepOneError(true);
        return false;
      } else if (selectedCampaign === "option2") {
        if (audienceError) {
          return false;
        } else if (!selectedSegment) {
          setAlertObj({
            active: true,
            type: "error",
            msg: "Please select a segment to proceed",
          });
          setLoading(false);
          setStepOneError(true);
          return false;
        }
      } else if (selectedCampaign === "option1" && !csvFile) {
        setAlertObj({
          active: true,
          type: "error",
          msg: "Please select a CSV file to proceed",
        });
        setLoading(false);
        setStepOneError(true);
        return false;
      }
      return !!selectedCampaign;
  };

  const handleNext = () => {
    console.log(audienceError, "Audience error");
    if (activeStep === 1 && !validateAudience()) return;
    setAlertObj({
      active: false,
      type: "",
      msg: "",
    });
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Campaigns (Beta) single page: run every step's checks, then the normal submit
  const handleSinglePageSubmit = () => {
    const fail = (msg) => setAlertObj({ active: true, type: "error", msg });
    if (!singlePage.validateSetup()) return;
    if (!selectedTemplate) return fail("Please select a template");
    if (isEditMode) {
      // Edit sends only what changed (template, schedule, new CSV / segment); variables keep their saved values
      if (selectedCampaign === "option2" && !selectedSegment) return fail("Please select a segment");
      if (!scheduleDate || !broadcastTime) return fail("Please pick the date and time to send");
      return handleSubmit();
    }
    if (!validateTemplateFields(templateFormRef.current)) return;
    if (!selectedCampaign) return fail("Please choose the audience");
    if (!validateAudience()) return;
    if (broadcastType === "one-time" && (!scheduleDate || !broadcastTime)) return fail("Please pick the date and time to send");
    const fallbackProblem = isEnableCheckBoxVisible ? fallbackError(fallback) : null;
    if (fallbackProblem) return fail(fallbackProblem);
    handleSubmit();
  };

  const getFileInput = (type) => {
    const fileTypes = {
      image: { accept: ".jpg, .png", label: "Choose .JPG or .PNG *" },
      video: { accept: ".mp4", label: "Choose .MP4 file *" },
      document: { accept: ".pdf", label: "Choose .PDF file *" },
      gif: { accept: ".gif", label: "Choose .GIF file *" },
    };
    if (fileTypes[type]) {
      const { accept, label } = fileTypes[type];
      return (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <input
              ref={fileInputRef}
              id="file-upload"
              name="uploadedFile"
              type="file"
              accept={accept}
              onChange={(e) => {
                e.preventDefault();
                handleMediaFileChange(e);
              }}
              style={{ display: "none" }}
              required
            />
            <label htmlFor="file-upload">
              <Button
                variant="contained"
                component="span"
                sx={{ marginTop: 1, marginBottom: 1 }}
              >
                {label}
              </Button>
            </label>
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  const FileUploadDetails = () => {
    if (!mediaFile && !mediaFileError) {
      return null;
    }
    return (
      <Grid container direction="column" spacing={1}>
        {mediaFileError && (
          <Grid item>
            <Typography variant="body2" color="error" sx={{ display: "block" }}>
              {mediaFileError}
            </Typography>
          </Grid>
        )}
        {mediaFile && (
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Typography variant="body2">
                  File uploaded: {mediaFile.name}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  aria-label="delete"
                  onClick={handleDeleteMediaFile}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const CarouselFileUploadDetails = ({ cardIndex, file, onDelete }) => {
    if (!file) {
      return null;
    }
    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Typography variant="body2">File uploaded: {file.name}</Typography>
        </Grid>
        <Grid item>
          <IconButton
            aria-label="delete"
            onClick={() => onDelete(cardIndex)}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const checkBroadcastButtonDisabled = () => {
    if (loading) {
      return true;
    }
    if (selectedCampaign === "option2") {
      if (selectedSegmentList.length && phoneNumberCount === 0) {
        return true;
      }
    }
    if (successResponse) {
      return true;
    }
  };

  useEffect(() => {
    fetchCampaignsData();
  }, []);

  useEffect(() => {
    setRequiredFieldsToRender([...requiredFieldsToRender]);
  }, [mediaType, mediaFile, selectedTemplate, formValues, csvFile]);

  useEffect(() => {
    if (!selectedTemplate) return;
    updateRequiredFields(selectedTemplate, templatePreviewData);
  }, [selectedTemplate, templatePreviewData]);

  const handleCustomFieldChange = (type, index, e) => {
    const { name, value } = e.target;
    setTemplatePreviewData((prev) => {
      const updatedData = { ...prev };
      switch (type) {
        case "header":
          updatedData.sampleHeaderFields = updatedData.sampleHeaderFields.map(
            (field, i) =>
              i === index && field.type === "custom"
                ? { ...field, value }
                : field,
          );
          break;
        case "body":
          updatedData.sampleBodyFields = updatedData.sampleBodyFields.map(
            (field, i) =>
              i === index && field.type === "custom"
                ? { ...field, value }
                : field,
          );
          break;
        case "url":
          updatedData.allButtons = updatedData.allButtons.map((button, i) =>
            i === index
              ? { ...button, data: { ...button.data, sampleUrl: value } }
              : button,
          );
          break;
        default:
          break;
      }
      return updatedData;
    });
    handleInputChange(e);
  };

  const sortOptions = [
    { value: "date-desc", label: "Date (Newest First)" },
    { value: "date-asc", label: "Date (Oldest First)" },
    { value: "name-asc", label: "Name (A-Z)" },
    { value: "name-desc", label: "Name (Z-A)" },
  ];

  const currentSortLabel = sortOptions.find(
    (option) => option.value === sortBy,
  )?.label;

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  function formatTwoDecimals(value) {
    if (typeof value !== "number") return value;
    return Math.floor(value * 100) / 100;
  }

  const { date: editScheduleDate, time: editBroadcastTime } = useMemo(() => {
    if (isEditMode && props?.pageParams?.broadcast?.scheduled) {
      return parseScheduledDateTime(props.pageParams.broadcast.scheduled);
    }
    return { date: null, time: null };
  }, [isEditMode, props?.pageParams]);

  const renderCustomFields = () => {
    const placeholderHeaderFields =
      templatePreviewData?.sampleHeaderFields || [];
    const placeholderBodyFields = templatePreviewData?.sampleBodyFields || [];
    const placeholderURLFields = Array.isArray(templatePreviewData?.allButtons)
      ? templatePreviewData.allButtons
          .filter(
            (button) =>
              button?.type &&
              button?.type === "visit-website" &&
              button?.data?.sampleUrl,
          )
          .map((button) => button?.data?.sampleUrl)
      : [];

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
        {!singlePage && placeholderHeaderFields.length > 0 && (
          <Typography
            sx={{ fontSize: 16, fontWeight: 600, color: "#1B2232", mb: 2 }}
          >
            Header Parameters
          </Typography>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {!singlePage &&
            placeholderHeaderFields
              .filter((field) => field.type === "custom")
              .map((field, index) => (
                <Grid item xs={6} key={`header-${index}`}>
                  <TextField
                    fullWidth
                    label="Header Parameter"
                    name="hparameter"
                    value={field.value || ""}
                    onChange={(e) =>
                      handleCustomFieldChange("header", index, e)
                    }
                    required
                  />
                </Grid>
              ))}
        </Grid>

        {singlePage && (placeholderHeaderFields.length > 0 || placeholderBodyFields.length > 0) && (
          <VariablesPanel
            rows={[
              ...placeholderHeaderFields.slice(0, 1).map((field) =>
                field.type === "custom"
                  ? {
                      key: "H1",
                      label: "Header parameter",
                      inputProps: {
                        name: "hparameter",
                        placeholder: "Enter value",
                        value: field.value || "",
                        onChange: (e) => handleCustomFieldChange("header", 0, e),
                      },
                    }
                  : {
                      key: "H1",
                      label: `Header: ${String(field.type || "field").replaceAll("_", " ")}`,
                      fixed: field.type || "contact field",
                    },
              ),
              ...placeholderBodyFields.map((field, originalIndex) =>
              field.type === "custom"
                ? {
                    key: originalIndex + 1,
                    label: `Body parameter ${originalIndex + 1}`,
                    inputProps: {
                      name: `bparameter_${originalIndex}`,
                      placeholder: "Enter value",
                      value: field.value || "",
                      onChange: (e) =>
                        handleCustomFieldChange("body", originalIndex, e),
                    },
                  }
                : {
                    key: originalIndex + 1,
                    label: String(field.type || "field").replaceAll("_", " ").replace(/^\w/, (c) => c.toUpperCase()),
                    fixed: field.type || "contact field",
                  },
              ),
            ]}
          />
        )}
        {!singlePage && placeholderBodyFields.filter((field) => field.type === "custom")
          .length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#1B2232" }}
            >
              Body Parameters
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: "#6C7C93",
                mt: "6px",
              }}
            >
              Fill in the dynamic variables used in this template.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {!singlePage &&
            placeholderBodyFields
              .map((field, originalIndex) => ({ ...field, originalIndex }))
              .filter((field) => field.type === "custom")
              .map((field) => (
                <Grid item xs={6} key={`body-${field.originalIndex}`}>
                  <TextField
                    fullWidth
                    label={`Body Parameter ${field.originalIndex + 1}`}
                    name={`bparameter_${field.originalIndex}`}
                    value={field.value || ""}
                    onChange={(e) =>
                      handleCustomFieldChange("body", field.originalIndex, e)
                    }
                    required
                  />
                </Grid>
              ))}
        </Grid>

        {placeholderURLFields.length > 0 && (
          <Typography
            sx={{ fontSize: 16, fontWeight: 600, color: "#1B2232", mb: 1 }}
          >
            URL Parameters
          </Typography>
        )}
        <Grid container spacing={2}>
          {placeholderURLFields &&
            placeholderURLFields.map((field, index) => (
              <Grid item xs={6} key={`url-${index}`}>
                <TextField
                  fullWidth
                  label={`URL Parameter ${index + 1}`}
                  name={`urlparameter_${index}`}
                  value={field || ""}
                  onChange={(e) => handleCustomFieldChange("url", index, e)}
                  required
                />
              </Grid>
            ))}
        </Grid>
      </Box>
    );
  };

  const isEnableCheckBoxVisible =
    !isEditMode &&
    !loading &&
    selectedCampaign !== "" &&
    selectedCampaign !== "option3" &&
    (broadcastType === "now" || broadcastType === "one-time");

  // Navigation buttons component - only shown for non-retry
  const NavigationButtons = () => {
    if (categoryFilter === "retry") return null;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 3,
          pt: 2,
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <ButtonTwo
            variant="outlined"
            onClick={() => goToBroadcastList()}
            sx={{
              borderColor: "#D1D5DB",
              minWidth: 85,
              color: "#6B7280",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
              },
            }}
            title="Cancel"
          >
            Cancel
          </ButtonTwo>
          {activeStep > 0 && <BackButton onClick={handleBack} />}
        </Box>
        <Box>
          {activeStep < steps.length - 1 ? (
            <ButtonOne
              variant="contained"
              onClick={handleNext}
              disabled={
                activeStep === 0 ? !selectedTemplate : !selectedCampaign
              }
              sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}
            >
              {activeStep === 0 ? "Next" : "Continue to Broadcast Type"}
            </ButtonOne>
          ) : (
            <ButtonOne
              variant="contained"
              onClick={handleSubmit}
              disabled={checkBroadcastButtonDisabled()}
              sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : broadcastType === "one-time" ? (
                isEditMode ? (
                  "Update Broadcast"
                ) : (
                  "Schedule Broadcast"
                )
              ) : (
                "Broadcast"
              )}
            </ButtonOne>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth={false} disableGutters={!!singlePage}>
      <Box sx={{ padding: singlePage ? 0 : 2 }}>
        {singlePage ? (
          <BroadcastSections
            channel="whatsapp"
            category={{
              value: categoryFilter,
              onChange: (value) => {
                handleTemplateChange({ target: { value: "" } });
                setCategoryFilter(value);
              },
            }}
            templateOptions={templateOptions}
            getTemplateLabel={(template) => template.template_name}
            selectedTemplate={templateOptions.find((t) => t.template_name === selectedTemplate) || null}
            onTemplateSelect={(template) => handleTemplateChange({ target: { value: template ? template.template_name : "" } })}
            templateMeta={(() => {
              const t = templateOptions.find((item) => item.template_name === selectedTemplate);
              return t ? [
                { label: "Category", value: t.category },
                { label: "Language", value: t.language || t.template_preview_json?.language },
              ] : [];
            })()}
            templateNote="Only approved WhatsApp templates are listed."
            templateFormRef={templateFormRef}
            templateFields={
              isEditMode ? (
                <Typography sx={{ ...typography.bodySmall, color: "#6B7280", bgcolor: "#F6F7F9", borderRadius: "8px", px: 1.5, py: 1.1 }}>
                  Variable values and media stay as set when this broadcast was scheduled.
                </Typography>
              ) : (
                <>
                  <FileUploadDetails />
                  {renderCustomFields()}
                </>
              )
            }
            preview={<TemplateMessagePreview templatePreviewData={templatePreviewData} carouselMediaFiles={carouselMediaFiles} />}
            audienceTitle={singlePage.quick ? "Recipient" : "Audience"}
            hideSchedule={!!singlePage.quick}
            scheduleOnly={isEditMode}
            audience={
              isEditMode && isRetryEdit ? (
                <Typography sx={{ ...typography.bodySmall, color: "#6B7280" }}>
                  Failed recipients of the original broadcast. The audience of a retry can't be changed.
                </Typography>
              ) : singlePage.quick ? (
                <SingleContact
                  setCountryCode={setCountryCode}
                  setDialCode={setDialCode}
                  setName={setName}
                  nameValue={name}
                  countryCodeValue={countryCode}
                  phoneNumberValue={dialCode}
                  isEditMode={isEditMode}
                />
              ) : (
              <AudienceTypePicker
                disabled={isEditMode}
                note={isEditMode && selectedCampaign === "option1" ? "Upload a new CSV only to replace the current list." : null}
                value={{ option1: "csv", option2: "segment" }[selectedCampaign] || ""}
                onChange={(value) => setSelectedCampaign(value === "csv" ? "option1" : "option2")}
                csv={
                  <UploadCSV
                    ref={csvInputRef}
                    csvFile={csvFile}
                    setCsvFile={setCsvFile}
                    campaignsData={campaignsData}
                    isEditMode={isEditMode}
                  />
                }
                segment={
                  <SelectSegment
                    audienceError={audienceError}
                    setAudienceError={setAudienceError}
                    prefetchedSegmentName={props?.pageParams?.segmentName}
                    isEditMode={isEditMode}
                    selectedSegment={selectedSegment}
                    setSelectedSegment={setSelectedSegment}
                    phoneNumberCount={phoneNumberCount}
                    setPhoneNumberCount={setPhoneNumberCount}
                    campaignsData={campaignsData}
                  />
                }
              />
              )
            }
            audienceSummary={
              isEditMode && isRetryEdit
                ? "Failed recipients"
                : selectedCampaign === "option1"
                ? csvFile?.name || (isEditMode ? "Current list (unchanged)" : null)
                : selectedCampaign === "option2" && selectedSegment
                  ? `${selectedSegment.name || selectedSegment.segment_name || "Segment"}${phoneNumberCount ? ` · ${phoneNumberCount} contacts` : ""}`
                  : selectedCampaign === "option3" && dialCode
                    ? `${countryCode} ${dialCode}`
                    : null
            }
            isLater={broadcastType === "one-time"}
            onSendNow={() => {
              setBroadcastType("now");
              setScheduleDate("");
              setBroadcastTime("");
            }}
            onSendLater={() => setBroadcastType("one-time")}
            scheduleDate={scheduleDate}
            scheduleTime={broadcastTime}
            onScheduleApply={(date, time) => {
              setScheduleDate(date);
              setBroadcastTime(time);
            }}
            showRetry={isEnableCheckBoxVisible}
            fallback={isEnableCheckBoxVisible ? fallback : undefined}
            setFallback={setFallback}
            enableTimeSlot={enableTimeSlot}
            setEnableTimeSlot={setEnableTimeSlot}
            timeSlot={timeSlot}
            setTimeSlot={setTimeSlot}
            submitLabel={singlePage.submitLabel}
            onCancel={() => goToBroadcastList()}
            onSubmit={handleSinglePageSubmit}
            loading={loading || successResponse}
          />
        ) : (
          <>
        {/* Tabs - Full Width */}
        <Grid item xs={12}>
          <Box sx={{ mb: 3 }}>
            <CustomTabs
              value={categoryFilter}
              onChange={handleCategoryFilterChange}
              tabs={[
                {
                  label: "Marketing",
                  value: "marketing",
                  disabled: isEditMode || fromTables,
                },
                {
                  label: "Utility",
                  value: "utility",
                  disabled: isEditMode || fromTables,
                },
                {
                  label: "Retry Failed",
                  value: "retry",
                  disabled: isEditMode || fromTables,
                },
              ]}
            />
          </Box>
        </Grid>

        {fromTables && (
          <Box sx={{ mb: 3 }}>
            <BackButton onClick={() => props.onNavigate("view_templates")} />
          </Box>
        )}

        {fromBroadcastCampaign && (
          <Box sx={{ mb: 3 }}>
            <BackButton
              onClick={() => goToBroadcastList()}
            />
          </Box>
        )}

        {/* Stepper - Only shown for non-retry */}
        {categoryFilter !== "retry" && (
          <Box sx={{ mb: 4, width: "100%" }}>
            <AppStepper steps={steps} activeStep={activeStep} />
          </Box>
        )}

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Left side - Form Content */}
          <Grid
            item
            xs={12}
            md={
              categoryFilter !== "retry" && activeStep >= 1 && selectedTemplate
                ? 8
                : 12
            }
          >
            {/* Retry Failed Tab - Show full screen component */}
            {categoryFilter === "retry" ? (
              <RetryFailedBroadcast
                scheduleDate={scheduleDate}
                setScheduleDate={setScheduleDate}
                broadcastTime={broadcastTime}
                setBroadcastTime={setBroadcastTime}
                onNavigate={props.onNavigate}
                onPreviewTemplate={handleRetryTemplatePreview}
              />
            ) : (
              /* Marketing/Utility Tabs - Show step-based UI */
              <>
                {/* Step 0: Template Selection */}
                {activeStep === 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #DFE5EC",
                      p: 3,
                      borderRadius: "12px",
                      minHeight: 500,
                    }}
                  >
                    <form id="step1-form" onSubmit={handleCampaignClick}>
                      <StepTemplateSelection
                        categoryFilter={categoryFilter}
                        fromTables={fromTables}
                        props={props}
                        filteredAndSortedTemplates={filteredAndSortedTemplates}
                        templatesLoading={templatesLoading}
                        viewMode={viewMode}
                        sortAnchorEl={sortAnchorEl}
                        setSortAnchorEl={setSortAnchorEl}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        currentSortLabel={currentSortLabel}
                        sortOptions={sortOptions}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        selectedTemplate={selectedTemplate}
                        templateCardRefs={templateCardRefs}
                        handleTemplateChange={handleTemplateChange}
                        formatReadableDate={formatReadableDate}
                        handleTemplateSelect={handleTemplateSelect}
                        isEditMode={isEditMode}
                        scheduleDate={scheduleDate}
                        setScheduleDate={setScheduleDate}
                        broadcastTime={broadcastTime}
                        setBroadcastTime={setBroadcastTime}
                        handleCampaignClick={handleCampaignClick}
                      />
                    </form>
                  </Paper>
                )}

                {/* Step 1: Campaign Selection */}
                {activeStep === 1 && selectedTemplate && isCampaignTypeReady && (
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #DFE5EC",
                      p: 3,
                      borderRadius: "12px",
                      // minHeight: 500,
                    }}
                  >
                    <StepCampaignSelection
                      selectedCampaign={selectedCampaign}
                      setSelectedCampaign={setSelectedCampaign}
                      selectedTemplate={selectedTemplate}
                      csvFile={csvFile}
                      setCsvFile={setCsvFile}
                      campaignsData={campaignsData}
                      selectedSegment={selectedSegment}
                      setSelectedSegment={setSelectedSegment}
                      phoneNumberCount={phoneNumberCount}
                      setPhoneNumberCount={setPhoneNumberCount}
                      setCountryCode={setCountryCode}
                      setDialCode={setDialCode}
                      setName={setName}
                      name={name}
                      countryCode={countryCode}
                      dialCode={dialCode}
                      csvInputRef={csvInputRef}
                      isEditMode={isEditMode}
                      props={props}
                      campaignType={campaignType}
                      audienceError={audienceError}
                      setAudienceError={setAudienceError}
                    />
                  </Paper>
                )}

                {/* Step 2: Broadcast Type Selection */}
                {activeStep === 2 && selectedTemplate && selectedCampaign && (
                  <form id="step3-form" onSubmit={handleSubmit}>
                    <StepBroadcastSelection
                      broadcastType={broadcastType}
                      handleBroadcastTypeChange={handleBroadcastTypeChange}
                      isMobile={isMobile}
                      isEditMode={isEditMode}
                      selectedCampaign={selectedCampaign}
                      isEnableCheckBoxVisible={isEnableCheckBoxVisible}
                      enableTimeSlot={enableTimeSlot}
                      setEnableTimeSlot={setEnableTimeSlot}
                      timeSlot={timeSlot}
                      setTimeSlot={setTimeSlot}
                      checkBroadcastButtonDisabled={
                        checkBroadcastButtonDisabled
                      }
                      loading={loading}
                      handleSubmit={handleSubmit}
                      handleBack={handleBack}
                      walletErrorAcknowledged={walletErrorAcknowledged}
                      setWalletErrorAcknowledged={setWalletErrorAcknowledged}
                      walletValidation={walletValidation}
                      campaignName={campaignName}
                      setCampaignName={setCampaignName}
                      campaignDescription={campaignDescription}
                      setCampaignDescription={setCampaignDescription}
                      scheduleDate={scheduleDate}
                      setScheduleDate={setScheduleDate}
                      broadcastTime={broadcastTime}
                      setBroadcastTime={setBroadcastTime}
                      notifyBefore={notifyBefore}
                      setNotifyBefore={setNotifyBefore}
                      notifyStatus={notifyStatus}
                      setNotifyStatus={setNotifyStatus}
                      campaignsData={campaignsData}
                      setLoading={setLoading}
                      formatTwoDecimals={formatTwoDecimals}
                      pageParams={props.pageParams}
                      walletErrorDialog={walletErrorDialog}
                      setWalletErrorDialog={setWalletErrorDialog}
                      scheduleError={scheduleError}
                    />
                  </form>
                )}
              </>
            )}
          </Grid>

          {/* Right side - Message Preview (only visible for non-retry in Steps 1-2) */}
          {categoryFilter !== "retry" &&
            activeStep >= 1 &&
            selectedTemplate && (
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    bgcolor: "#F9FAFB",
                    borderRadius: "12px",
                    height: "100%",
                    // minHeight: 500,
                    position: "sticky",
                    top: 0,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      height: "100%",
                      width: "100%",
                      "& > *": {
                        maxWidth: "100%",
                        overflowX: "hidden",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      },
                    }}
                  >
                    {templatePreviewData?.marketingOption === "carousel" ? (
                      <MessageNode
                        templateName={templatePreviewData.templateName}
                        language={templatePreviewData.language}
                        headerType="none"
                        headerText=""
                        headerFile={null}
                        body=""
                        sampleBodyFields={[]}
                        footer=""
                        mode="view"
                        allButtons={[]}
                        marketingOption="carousel"
                        carouselCards={templatePreviewData.carouselCards}
                        carouselMainBody={templatePreviewData.carouselMainBody}
                        carouselMainBodySampleFields={
                          templatePreviewData.carouselMainBodySampleFields || []
                        }
                        {...Object.keys(templatePreviewData)
                          .filter((key) => key.startsWith("cardFile_"))
                          .reduce((acc, key) => {
                            acc[key] = templatePreviewData[key];
                            return acc;
                          }, {})}
                        {...Object.keys(carouselMediaFiles).reduce(
                          (acc, index) => {
                            acc[`cardFile_${index}`] =
                              carouselMediaFiles[index];
                            return acc;
                          },
                          {},
                        )}
                        key={`carousel-preview-${JSON.stringify(Object.keys(carouselMediaFiles))}`}
                        sx={{
                          maxWidth: "100%",
                          overflowX: "hidden",
                          "& .MuiCard-root": {
                            maxWidth: "100%",
                          },
                          "& img": {
                            maxWidth: "100%",
                            height: "auto",
                          },
                        }}
                      />
                    ) : (
                      <MessageNode
                        templateName={templatePreviewData?.template_name || ""}
                        marketingOption={
                          templatePreviewData?.marketingOption || null
                        }
                        language={templatePreviewData?.language || "english"}
                        headerType={templatePreviewData?.headerType || "none"}
                        headerText={templatePreviewData?.headerText || ""}
                        headerFile={templatePreviewData?.headerFile || null}
                        body={
                          templatePreviewData?.body || "No template selected"
                        }
                        sampleBodyFields={
                          templatePreviewData?.sampleBodyFields || []
                        }
                        sampleHeaderFields={
                          templatePreviewData?.sampleHeaderFields || []
                        }
                        footer={templatePreviewData?.footer || ""}
                        allButtons={templatePreviewData?.allButtons || []}
                        mode="view"
                        location={templatePreviewData?.location || null}
                        showSampleValues
                        productCount={getMpmProductCount(
                          templatePreviewData?.mpmSections,
                        )}
                        thumbnailImage={getMpmThumbnailImage(
                          templatePreviewData?.mpmThumbnail || templatePreviewData?.spmProduct,
                        )}
                        productName={getSpmProductName(templatePreviewData?.spmProduct)}
                        {...authPreviewProps(templatePreviewData)}
                        sx={{
                          maxWidth: "100%",
                          overflowX: "hidden",
                          "& .MuiCard-root": {
                            maxWidth: "100%",
                          },
                          "& img": {
                            maxWidth: "100%",
                            height: "auto",
                          },
                          "& table": {
                            maxWidth: "100%",
                            overflowX: "hidden",
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
        </Grid>

        {/* Navigation Buttons - Only for non-retry */}
        <NavigationButtons />
          </>
        )}

        {isSlowProcessing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Processing in progress</AlertTitle>
            <Typography variant="body2">
              This is a large file upload. You will be notified via email once
              the processing is complete.
            </Typography>
          </Alert>
        )}

        <Grid item xs={12} sx={{ marginTop: 2 }}>
          {alertObj.active && (
            <Alert severity={alertObj.type} onClose={handleAlertClose}>
              {alertObj.msg}
            </Alert>
          )}

          {csvError && (
            <Alert severity="error" onClose={handleCSVErrorClose}>
              <Typography fontWeight="bold">{csvError.summary}</Typography>
              {csvError.details.length > 0 && (
                <ul>
                  {(showAllErrors
                    ? csvError.details
                    : csvError.details.slice(0, 5)
                  ).map((errMsg, index) => (
                    <li key={index}>{errMsg}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </Grid>

        {/* Template Preview Modal */}
        <TemplatePreviewModal
          open={modalOpen}
          onClose={handleCloseModal}
          templatePreviewData={templatePreviewData}
          carouselMediaFiles={carouselMediaFiles}
          renderCustomFields={renderCustomFields}
          FileUploadDetails={FileUploadDetails}
          selectedTemplate={selectedTemplate}
          handleCampaignClick={handleCampaignClick}
        />

        <WalletErrorDialog
          open={walletErrorDialog.open}
          data={walletErrorDialog.data}
          onClose={() => setWalletErrorDialog({ open: false, data: null })}
          onRecharge={() => {
            setWalletErrorDialog({ open: false, data: null });
          }}
        />
      </Box>

      <CampaignTypeModal
        open={campaignTypeModalOpen}
        onClose={handleCloseCampaignTypeModal}
        campaignType={campaignType}
        setCampaignType={setCampaignType}
        onQuickSelect={() => {
          setCampaignType("quick");
          setCampaignTypeModalOpen(false);
          setIsCampaignTypeReady(true);
        }}
        onStandardSelect={() => {
          setCampaignType("standard");
          setCampaignTypeModalOpen(false);
          setCampaignModalOpen(true);
        }}
      />
      <CreateCampaignModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        campaignName={campaignName}
        campaignDescription={campaignDescription}
        onNameChange={handleCampaignNameChange}
        onDescriptionChange={handleCampaignDescriptionChange}
        nameError={campaignNameError}
        nameCharCount={campaignNameCharCount}
        descCharCount={campaignDescCharCount}
        onSubmit={handleCreateCampaign}
      />
    </Container>
  );
};

export default React.memo(BroadcastTemplates);
